// CustomPricing.tsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';

import { Button, Typography, Space, Spin, message, Radio, Tag, Tooltip, Modal, Alert, Select, InputNumber, Input } from 'antd';

import { CheckCircleOutlined, CrownOutlined, ArrowUpOutlined, ArrowDownOutlined, RocketOutlined, CloseCircleOutlined, CreditCardOutlined, CalendarOutlined, DollarOutlined } from '@ant-design/icons';

import axios from './utils/accountApi';
import { getAuthHeaders } from './utils/authHeaders';
import LoadingCard from './components/LoadingCard';
import './CustomPricing.css';

import * as fbPixel from './utils/fbPixel';

import { getStripePromise } from './utils/stripe';
import { generateRevenueFunnelId, trackRevenuePricingIntent } from './utils/revenueHealth';

import { getFacebookCookies } from './utils/trackingCookies';
import {
  chooseCreditTopUpRecommendation,
  type ForeverPlanRecommendationInput,
} from './components/creditTopUpRecommendation';



const { Title, Text, Paragraph, Link } = Typography;

type BillingInterval = 'monthly' | 'yearly';



interface SubscriptionPlanMetadata {
  key: string;
  displayName: string;
  order: number;
  tier: number;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyCredits: number;
  totalAnnualCredits: number;
  foreverCreditDiscountPercent: number;
  idealFor?: string;
  highlight?: boolean;
  badgeText?: string;
  badgeColor?: string;
  tagline?: string;
  isLegacy?: boolean;
  billingInterval?: string;
  intervalMonths?: number;
}

interface SubscriptionPlanGroup {
  key: string;
  monthly?: Plan;
  yearly?: Plan;
  metadata: SubscriptionPlanMetadata;
}

interface ResolvedForeverPlanMetadata {
  displayName: string;
  order: number;
  credits: number;
  price: number;
  badgeText?: string;
  badgeColor?: string;
  highlight?: boolean;
}

interface ReferralStatusResponse {
  appliedCode?: string | null;
  referrerUserId?: string | null;
  referrerDisplayName?: string | null;
  referralCreditsStatus?: string | null;
  referralCodeAppliedAt?: string | null;
  referralEligibilityLocked?: boolean;
  eligibility?: string;
  canApply?: boolean;
  ineligibleReason?: string | null;
  referralRewardChecked?: boolean;
  referralRewardResult?: string | null;
}

const API_BASE = resolveFrontendApiBase();

const REFERRAL_STORAGE_KEY = 'scribeshadow_referral_code';

const pickDefined = <T,>(...values: Array<T | null | undefined>): T | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

const ensureNumber = (value: any, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const ensureString = (value: any, fallback = ''): string => {
  if (value === undefined || value === null) {
    return fallback;
  }
  return String(value);
};

const ensureBoolean = (value: any, fallback = false): boolean => {
  if (value === undefined || value === null) {
    return fallback;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no'].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
};

const ensureMetadata = (value: any): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {}
);

const isOnBreakPlan = (plan: any): boolean => {
  const metadata = ensureMetadata(plan?.metadata);
  return Boolean(
    metadata.isBreakPlan ||
      plan?.planKey === 'on_break' ||
      plan?.id === 'on_break'
  );
};

const sanitizeAffiliateCode = (value: string): string =>
  value
    ? value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 15)
    : '';

export const clampForeverQuantity = (value: number | null | undefined): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 1;
  }
  const rounded = Math.round(numeric);
  return Math.min(Math.max(rounded, 1), 10);
};

const resolveForeverQuantityKey = (plan: Plan, fallbackId?: string): string => {
  const candidates: Array<any> = [
    (plan as any)?.id,
    plan.stripePriceID,
    (plan as any)?.stripePriceId,
    plan.planKey,
    plan.planName,
    fallbackId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return fallbackId || 'forever-credits-plan';
};

interface HumanTranslationRateBand {
  key: string;
  label: string;
  low: number;
  high: number;
}

interface SavingsCalculatorContext {
  planName: string;
  planPrice: number;
  planCredits: number;
  intervalLabel: string;
}

const surfaceInfo = 'var(--surface-info)';
const borderInfo = 'var(--border-info)';
const accentPrimary = 'var(--accent-primary)';
const accentPrimaryHover = 'var(--accent-primary-hover)';
const accentSuccess = 'var(--accent-success)';
const accentWarning = 'var(--accent-warning)';
const accentDanger = 'var(--accent-danger)';

const resolvePlanKey = (rawPlan: any): string => {
  const candidate = pickDefined(
    rawPlan?.planKey,
    rawPlan?.plan_key,
    rawPlan?.plan_name,
    rawPlan?.planName,
    rawPlan?.displayName
  );
  if (candidate && typeof candidate === 'string' && candidate.trim().length > 0) {
    return candidate.trim();
  }
  const fallbackName = ensureString(pickDefined(rawPlan?.planName, rawPlan?.displayName, rawPlan?.name, ''), '').trim();
  const normalized = normalizePlanName(fallbackName);
  return normalized.length > 0 ? normalized : fallbackName || 'plan';
};

const computeMonthlyCreditsFromPlan = (plan?: Plan): number | undefined => {
  if (!plan) {
    return undefined;
  }

  if (plan.billingInterval === 'monthly') {
    const candidate = pickDefined(plan.monthlyCredits, plan.creditsPerInterval, plan.monthlyEquivalentCredits);
    if (candidate !== undefined) {
      return ensureNumber(candidate, 0);
    }
  }

  if (plan.billingInterval === 'yearly') {
    const explicitMonthly = pickDefined(plan.monthlyEquivalentCredits, ensureNumber(plan.creditsPerInterval, 0) / Math.max(plan.intervalMonths || 12, 1));
    if (explicitMonthly !== undefined) {
      return ensureNumber(explicitMonthly, 0);
    }
  }

  const fallback = pickDefined(plan.monthlyCredits, plan.monthlyEquivalentCredits, plan.creditsPerInterval);
  return fallback !== undefined ? ensureNumber(fallback, 0) : undefined;
};

const computeAnnualCreditsFromPlan = (plan?: Plan, fallbackMonthly?: number): number | undefined => {
  if (!plan) {
    return fallbackMonthly !== undefined ? fallbackMonthly * 12 : undefined;
  }

  if (plan.billingInterval === 'yearly') {
    const annual = pickDefined(plan.creditsPerInterval, plan.monthlyCredits && plan.intervalMonths ? plan.monthlyCredits * plan.intervalMonths : undefined);
    if (annual !== undefined) {
      return ensureNumber(annual, 0);
    }
  }

  const monthly = computeMonthlyCreditsFromPlan(plan) ?? fallbackMonthly;
  return monthly !== undefined ? monthly * 12 : undefined;
};

const normalizeSubscriptionPlan = (rawPlan: any): Plan => {
  const metadata = ensureMetadata(rawPlan?.metadata);
  const planKey = resolvePlanKey(rawPlan);

  const intervalRaw = ensureString(
    pickDefined(rawPlan?.billingInterval, rawPlan?.interval, metadata.billingInterval, metadata.interval)
  )
    .toLowerCase()
    .trim();
  const billingInterval: BillingInterval = intervalRaw.startsWith('year') || intervalRaw.startsWith('annual')
    ? 'yearly'
    : 'monthly';

  const intervalMonths = ensureNumber(
    pickDefined(rawPlan?.intervalMonths, metadata.intervalMonths),
    billingInterval === 'yearly' ? 12 : 1
  );

  const creditsPerInterval = ensureNumber(
    pickDefined(rawPlan?.creditsPerInterval, rawPlan?.monthlyCredits, metadata.creditsPerInterval, metadata.monthlyCredits),
    0
  );

  const monthlyEquivalentExplicit = pickDefined(rawPlan?.monthlyEquivalentCredits, metadata.monthlyEquivalentCredits);
  const monthlyEquivalentCredits =
    monthlyEquivalentExplicit !== undefined
      ? ensureNumber(monthlyEquivalentExplicit, 0)
      : billingInterval === 'yearly'
      ? Math.floor(creditsPerInterval / Math.max(intervalMonths || 1, 1))
      : creditsPerInterval;

  const monthlyCredits = ensureNumber(
    pickDefined(rawPlan?.monthlyCredits, metadata.monthlyCredits, billingInterval === 'monthly' ? creditsPerInterval : monthlyEquivalentCredits),
    billingInterval === 'monthly' ? creditsPerInterval : monthlyEquivalentCredits
  );

  const resolvedPrice = ensureNumber(pickDefined(rawPlan?.price, rawPlan?.amount, metadata.price), 0);
  const sortOrder = ensureNumber(pickDefined(rawPlan?.sortOrder, metadata.order), 0);
  let tier = ensureNumber(pickDefined(rawPlan?.tier, metadata.tier), 0);
  if (!tier || tier <= 0) {
    if (sortOrder) {
      const derived = Math.floor(sortOrder / 10);
      if (derived > 0) {
        tier = derived;
      }
    }
  }
  if (!tier || tier <= 0) {
    if (monthlyCredits > 0) {
      tier = 1;
    } else {
      if (resolvedPrice > 0) {
        tier = 1;
      }
    }
  }

  return {
    id: ensureString(pickDefined(rawPlan?.id, rawPlan?.docId, rawPlan?.documentId, rawPlan?.documentID), `${planKey}-${billingInterval}`),
    planName: ensureString(pickDefined(rawPlan?.planName, rawPlan?.displayName, metadata.displayName, planKey), planKey),
    planKey,
    displayName: typeof rawPlan?.displayName === 'string' ? rawPlan.displayName : metadata.displayName,
    price: resolvedPrice,
    stripePriceID: ensureString(pickDefined(rawPlan?.stripePriceID, rawPlan?.stripePriceId), ''),
    stripeProductID: ensureString(pickDefined(rawPlan?.stripeProductID, rawPlan?.stripeProductId), ''),
    billingInterval,
    intervalMonths: Math.max(intervalMonths, 1),
    tier,
    monthlyCredits,
    creditsPerInterval,
    monthlyEquivalentCredits,
    currency: ensureString(pickDefined(rawPlan?.currency, rawPlan?.currencyCode, metadata.currency, 'usd'), 'usd').toLowerCase(),
    isActive: ensureBoolean(pickDefined(rawPlan?.isActive, metadata.isActive), true),
    isLegacy: ensureBoolean(pickDefined(rawPlan?.isLegacy, metadata.isLegacy), false),
    oneTimeCredits: rawPlan?.oneTimeCredits !== undefined ? ensureNumber(rawPlan.oneTimeCredits, 0) : undefined,
    description: typeof rawPlan?.description === 'string' ? rawPlan.description : metadata.description,
    sortOrder,
    metadata,
  };
};

const normalizeForeverPlan = (rawPlan: any): Plan => {
  const normalized = normalizeSubscriptionPlan({ ...rawPlan, billingInterval: rawPlan?.billingInterval ?? 'monthly' });
  const metadata = ensureMetadata(rawPlan?.metadata);
  const credits = ensureNumber(
    pickDefined(rawPlan?.oneTimeCredits, rawPlan?.credits, metadata.oneTimeCredits, metadata.credits),
    normalized.oneTimeCredits ?? normalized.creditsPerInterval ?? 0
  );

  return {
    ...normalized,
    billingInterval: 'monthly',
    intervalMonths: 1,
    monthlyCredits: 0,
    creditsPerInterval: credits,
    monthlyEquivalentCredits: credits,
    oneTimeCredits: credits,
    metadata: { ...normalized.metadata, ...metadata },
  };
};

const resolveSubscriptionMetadata = (
  key: string,
  monthly: Plan | undefined,
  yearly: Plan | undefined,
  groupPlans: Plan[]
): SubscriptionPlanMetadata => {
  const metadataSources = [
    ensureMetadata(monthly?.metadata),
    ensureMetadata(yearly?.metadata),
    ...groupPlans.map((plan) => ensureMetadata(plan.metadata)),
  ];

  const pickMeta = (...keys: string[]): any => {
    for (const meta of metadataSources) {
      if (!meta) {
        continue;
      }
      for (const candidateKey of keys) {
        if (candidateKey in meta) {
          const value = meta[candidateKey];
          if (value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '')) {
            return value;
          }
        }
      }
    }
    return undefined;
  };

  const displayName = ensureString(
    pickDefined(
      monthly?.displayName,
      yearly?.displayName,
      pickMeta('displayName', 'name', 'title'),
      monthly?.planName,
      yearly?.planName,
      key
    ),
    key
  );

  const order = ensureNumber(
    pickDefined(
      monthly?.sortOrder,
      yearly?.sortOrder,
      pickMeta('order', 'sortOrder', 'position', 'rank')
    ),
    0
  );

  const monthlyCreditsSource = pickDefined(
    computeMonthlyCreditsFromPlan(monthly),
    pickMeta('monthlyCredits', 'monthly_credits'),
    computeMonthlyCreditsFromPlan(yearly)
  );
  const monthlyCredits = ensureNumber(monthlyCreditsSource, 0);

  const rawTier = ensureNumber(
    pickDefined(
      monthly?.tier,
      yearly?.tier,
      pickMeta('tier', 'level', 'rank')
    ),
    0
  );
  let tier = rawTier;
  if (!tier || tier <= 0) {
    if (order) {
      const derived = Math.floor(order / 10);
      if (derived > 0) {
        tier = derived;
      }
    }
  }
  const creditsForTier = ensureNumber(
    pickDefined(
      monthly?.monthlyCredits,
      monthly?.monthlyEquivalentCredits,
      yearly?.monthlyEquivalentCredits
    ),
    monthlyCredits
  );
  if ((!tier || tier <= 0) && creditsForTier > 0) {
    tier = 1;
  }

  const monthlyPrice = ensureNumber(
    pickDefined(
      monthly?.price,
      pickMeta('monthlyPrice', 'monthly_price', 'priceMonthly')
    ),
    monthly?.price ?? 0
  );

  const yearlyPrice = ensureNumber(
    pickDefined(
      yearly?.price,
      pickMeta('yearlyPrice', 'annualPrice', 'annual_price', 'priceAnnual'),
      monthly?.price ? monthly.price * 12 : undefined
    ),
    (monthly?.price ?? monthlyPrice) * 12
  );

  if (!tier || tier <= 0) {
    if (monthlyPrice > 0 || yearlyPrice > 0) {
      tier = 1;
    }
  }

  const annualCreditsSource = pickDefined(
    computeAnnualCreditsFromPlan(yearly, monthlyCredits),
    pickMeta('totalAnnualCredits', 'annualCredits', 'annual_credits'),
    monthlyCredits > 0 ? monthlyCredits * 12 : undefined
  );
  const totalAnnualCredits = ensureNumber(annualCreditsSource, monthlyCredits * 12);

  const foreverCreditDiscountPercent = ensureNumber(
    pickDefined(
      pickMeta('foreverCreditDiscountPercent', 'foreverCreditDiscount', 'forever_credit_discount_percent', 'foreverCreditsDiscount')
    ),
    0
  );

  const idealFor = pickDefined(pickMeta('idealFor', 'ideal_for', 'audience', 'bestFor'));
  const highlight = ensureBoolean(pickDefined(pickMeta('highlight', 'isFeatured', 'featured', 'recommended')), false);
  const badgeText = pickDefined(pickMeta('badgeText', 'badge_text', 'badgeLabel', 'badge')); 
  const badgeColor = pickDefined(pickMeta('badgeColor', 'badge_color'));
  const tagline = pickDefined(pickMeta('tagline', 'subtitle', 'subheading'));
  const isLegacy = ensureBoolean(
    pickDefined(monthly?.isLegacy, yearly?.isLegacy, pickMeta('isLegacy')),
    false
  );

  return {
    key,
    displayName,
    order,
    tier,
    monthlyPrice,
    yearlyPrice,
    monthlyCredits,
    totalAnnualCredits,
    foreverCreditDiscountPercent,
    idealFor: idealFor ? ensureString(idealFor) : undefined,
    highlight,
    badgeText: badgeText ? ensureString(badgeText) : undefined,
    badgeColor: badgeColor ? ensureString(badgeColor) : undefined,
    tagline: tagline ? ensureString(tagline) : undefined,
    isLegacy,
  };
};

const HUMAN_TRANSLATION_RATE_BANDS: HumanTranslationRateBand[] = [

  { key: 'french', label: 'French', low: 0.06, high: 0.08 },

  { key: 'italian', label: 'Italian', low: 0.06, high: 0.08 },

  { key: 'spanish', label: 'Spanish', low: 0.06, high: 0.08 },

  { key: 'portuguese', label: 'Portuguese (Brazilian or European)', low: 0.06, high: 0.08 },

  { key: 'dutch', label: 'Dutch', low: 0.06, high: 0.08 },

  { key: 'german', label: 'German', low: 0.08, high: 0.12 },

  { key: 'swedish', label: 'Swedish', low: 0.10, high: 0.14 },

  { key: 'norwegian', label: 'Norwegian', low: 0.10, high: 0.14 },

  { key: 'polish', label: 'Polish', low: 0.08, high: 0.12 },

  { key: 'czech', label: 'Czech', low: 0.08, high: 0.12 },

  { key: 'russian', label: 'Russian', low: 0.08, high: 0.12 },

  { key: 'ukrainian', label: 'Ukrainian', low: 0.08, high: 0.12 },

  { key: 'hebrew', label: 'Hebrew', low: 0.10, high: 0.14 },

  { key: 'chinese_simplified', label: 'Chinese (Simplified)', low: 0.10, high: 0.14 },

  { key: 'chinese_traditional', label: 'Chinese (Traditional)', low: 0.10, high: 0.14 },

  { key: 'japanese', label: 'Japanese*', low: 0.13, high: 0.18 },

];







const TESTIMONIALS: { quote: string; author: string }[] = [
  {
    quote:
      "ScribeShadow's translation service helped me break into the German market with my first book launch. Within the first ten days of release, my book reached around #1000 in the Kindle store in Germany, earned over 12,000 KU page reads, and received multiple five-star reviews. This strong start has given me real momentum in a brand-new market, and I'm excited to keep growing my readership here. I couldn't have done it without the quality and reliability of ScribeShadow's translation.",
    author: 'JE Rowney',
  },
  {
    quote:
      "ScribeShadow is super-easy to use and extremely affordable. I immediately saw a nice jump in sales when I translated my books into German. Plus, their customer service is amazing! Very glad an author friend turned me on to them!",
    author: 'Riley Rose',
  },
  {
    quote:
      "I've been a full-time, self-published author for the last 11 years. It's been an exciting career that's had plenty of ups and downs. The last 18 months have been the toughest of all, and it was getting to the stage where writing books for a living no longer seemed sustainable. ScribeShadow has changed all that. Translating my books into multiple languages has made me very optimistic about the future, and, for the first time in a while, I feel hopeful that things are on the up once again.",
    author: 'George Mahood',
  },
  {
    quote:
      "We used ScribeShadow for the first time this month and we were so pleased with how easy it was to use. All 8 books in one series are now in German and ready for the market. We are hoping to carry on with translations in French, Italian and Spanish as well to really open our readership in more countries. Such an easy tool to use and the scope seems limitless!",
    author: 'Gemma Ford',
  },
  {
    quote:
      "I translated my entire 1.3-million-word series into French and German - they paid for themselves within 2 months and are now making more money than some of my English-speaking markets on Amazon. A great investment! Can't wait to do more languages.",
    author: 'Sara Creasy',
  },
  {
    quote:
      "I tried out ScribeShadow earlier this year after reading comments about it on an author group. I was initially worried to have my books translated, much less by AI. But now I can't look back. In the past, translating my books was a daunting task. Unthinkable. But now, I've translated most of my back-list into German and it now makes up 75% of my income. I can't wait to finish translating my back-list into German, and then try out other languages.",
    author: 'Nicholas Woode-Smith',
  },
  {
    quote:
      "I've translated five of my series into German and am thrilled with the sales and page reads I'm getting! I paid big bucks for a human translator and didn't make my money back. (Still haven't, lol). ScribeShadow has enabled me to share my stories with others around the globe. As soon as I finish my German translations, I plan to do both Dutch and Italian! Thank you ScribeShadow for providing a viable financial option for translations!",
    author: 'Laura Scott',
  },
  {
    quote:
      "I started using ScribeShadow earlier this month, and my translation income is over $1200 so far. The Amazon reviews are in the 4.5 star range. I am sorry I took so long to start using it!",
    author: 'Karen Brown',
  },
];

const TESTIMONIAL_ROTATION_INTERVAL_MS = 10000;
const WORDS_TICKER_REFRESH_MS = 5 * 60 * 1000;
const MIN_WORDS_PER_SECOND = 2;
const DEFAULT_WORDS_PER_SECOND = 12;
const WORDS_COUNTER_ENDPOINT = `${API_BASE}/public/word-count`;

const truncateQuote = (quote: string, limit = 550): string => {
  if (!quote) return '';
  if (quote.length <= limit) return quote;
  const trimmed = quote.slice(0, limit).trimEnd();
  return /[.!?]$/.test(trimmed) ? trimmed : trimmed + '...';
};
const normalizePlanName = (planName: string): string =>

  planName

    .replace(/annual|annually|yearly|monthly/gi, '')

    .replace(/plan/gi, '')

    .trim();






const formatCurrency = (amount: number): string =>

  amount.toLocaleString('en-US', {

    style: 'currency',

    currency: 'USD',

    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,

    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,

  });



const formatNumber = (value: number): string => value.toLocaleString('en-US');

 



export interface Plan {
  id: string;
  planName: string;
  planKey?: string;
  displayName?: string;
  price: number;
  stripePriceID: string;
  stripeProductID: string;
  billingInterval?: BillingInterval;
  intervalMonths?: number;
  tier?: number;
  monthlyCredits?: number;
  creditsPerInterval?: number;
  monthlyEquivalentCredits?: number;
  currency?: string;
  isActive?: boolean;
  isLegacy?: boolean;
  oneTimeCredits?: number;
  description?: string;
  sortOrder?: number;
  metadata?: Record<string, any>;
}

const derivePlanGroupKey = (plan: Plan): string => {
  const metadata = ensureMetadata(plan.metadata);
  const explicitGroupKey = ensureString(pickDefined(metadata.planGroupKey, metadata.groupKey), '').trim();
  const baseKey = explicitGroupKey || plan.planKey || normalizePlanName(plan.planName || '');
  if (!baseKey) {
    return '';
  }
  return baseKey.replace(/(?:[_-]?(annual|yearly))$/i, '');
};

const buildSubscriptionPlanGroups = (sourcePlans: Plan[]): SubscriptionPlanGroup[] => {
  if (!Array.isArray(sourcePlans) || sourcePlans.length === 0) {
    return [];
  }
  const map = new Map<string, { key: string; monthly?: Plan; yearly?: Plan; plans: Plan[] }>();
  sourcePlans.forEach((plan) => {
    const groupKey = derivePlanGroupKey(plan);
    if (!groupKey) {
      return;
    }
    const existing = map.get(groupKey) ?? { key: groupKey, plans: [] as Plan[] };
    if ((plan.billingInterval || 'monthly') === 'yearly') {
      existing.yearly = plan;
    } else {
      existing.monthly = plan;
    }
    existing.plans.push(plan);
    map.set(groupKey, existing);
  });

  const groups = Array.from(map.values()).map(({ key, monthly, yearly, plans }) => ({
    key,
    monthly,
    yearly,
    metadata: resolveSubscriptionMetadata(key, monthly, yearly, plans),
  }));

  return groups.sort((a, b) => {
    if (a.metadata.order !== b.metadata.order) {
      return a.metadata.order - b.metadata.order;
    }
    return a.metadata.displayName.localeCompare(b.metadata.displayName);
  });
};



interface CustomPricingProps {

  userID: string;

  email?: string;

  isForeverCredits?: boolean;

  onSuccess?: () => void;

  currentPlanTier?: number;
  currentPlanPriceId?: string;
  currentPlanIsLegacy?: boolean;
  currentPlanForeverDiscountPercent?: number;

  isAuth?: boolean;

  showLoginModal?: () => void;

  pendingChange?: {

    targetPriceId: string;

    targetProductId?: string;

    effectiveAt?: number;

    action?: 'upgrade' | 'downgrade';

  };

  // New: compact layout for forever credits (smaller cards)

  compactForeverGrid?: boolean;

  // New: render mode to return only cards (no outer container/banner)

  renderMode?: 'container' | 'cardsOnly' | 'foreverWithSide';

  // New: direct vs redirect purchase for forever credits

  purchaseMode?: 'redirect' | 'direct';
  includeSubscriberOnlyPlans?: boolean;
  pricingSurface?: 'account' | 'default';
  enableForeverQuantitySelection?: boolean;
  onForeverQuantityChange?: (event: ForeverQuantityChangeEvent) => void;

  // New: render forever credits with a right-side panel (current plan or subscribe)

  sidePanel?: {
    type: 'currentPlan' | 'subscribe';
    planName?: string;
    monthlyCreditsAllocated?: number;
    monthlyPrice?: number;
    billingInterval?: string;
    intervalMonths?: number;
    upfrontCredits?: number;
    nextRenewalDate?: number;
    onRenewNow?: () => void;
    onSubscribe?: () => void;
    pendingChange?: {
      targetPriceId?: string;
      targetProductId?: string;
      effectiveAt?: number;
      action?: 'upgrade' | 'downgrade';
    };
    renewDisabled?: boolean;
    renewDisabledTooltip?: string;
  };

  shortfallContext?: {
    source?: string;
    requiredCredits?: number;
    availableCredits?: number;
    shortfallCredits?: number;
    returnTo?: string;
  };

  beforeCheckout?: BeforeCheckoutHook;

}


interface CheckoutSessionOverrides {
  metadata?: Record<string, any>;
  payload?: Partial<CheckoutSessionRequest>;
}

export interface BeforeCheckoutContext {
  quantity: number;
  planId?: string | null;
  isForeverCredits: boolean;
  priceId?: string | null;
}

export interface ForeverQuantityChangeEvent {
  planId?: string | null;
  plan: Plan;
  quantity: number;
}

type BeforeCheckoutHook = (
  plan: Plan,
  context: BeforeCheckoutContext
) => Promise<CheckoutSessionOverrides | false | void> | CheckoutSessionOverrides | false | void;

export interface GiftCodeRequestPayload {
  recipientEmail?: string | null;
  recipientName?: string | null;
  giftMessage?: string | null;
}

interface CheckoutSessionRequest {
  priceId: string;
  userId?: string | null;
  email?: string | null;
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, any>;
  couponId?: string | null;
  isForeverCredits?: boolean;
  quantity?: number | null;
  isGiftPurchase?: boolean;
  giftRecipientEmail?: string | null;
  giftRecipientName?: string | null;
  giftSenderName?: string | null;
  giftMessage?: string | null;
  fbp?: string | null;
  fbc?: string | null;
  giftCodeRequests?: GiftCodeRequestPayload[];
  giftSplitMode?: 'single' | 'multi';
}



export const CustomPricing: React.FC<CustomPricingProps> = ({ 

  userID, 

  email,

  isForeverCredits = false,

  onSuccess,

  currentPlanTier = 0,
  currentPlanPriceId,

  currentPlanIsLegacy = false,
  currentPlanForeverDiscountPercent,

  isAuth = true,

  showLoginModal,

  pendingChange,

  compactForeverGrid = false,

  renderMode = 'container',

  purchaseMode = 'redirect',
  includeSubscriberOnlyPlans = false,
  pricingSurface = 'default',
  enableForeverQuantitySelection = false,
  onForeverQuantityChange,

  sidePanel,
  shortfallContext,

  beforeCheckout

}) => {

  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  const [subscriptionPlansLookup, setSubscriptionPlansLookup] = useState<Plan[]>([]);

  const [loading, setLoading] = useState(true);

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);

  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const [changeModalVisible, setChangeModalVisible] = useState(false);

  const [changeModalPlan, setChangeModalPlan] = useState<Plan | null>(null);

  const [changeAction, setChangeAction] = useState<'upgrade' | 'downgrade' | null>(null);

  const [selectedTiming, setSelectedTiming] = useState<'now' | 'next_cycle'>('now');

  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);

  const [loadingDetails, setLoadingDetails] = useState(false);

  const [submittingChange, setSubmittingChange] = useState(false);

  const [modalError, setModalError] = useState<string | null>(null);

  const [modalBlocked, setModalBlocked] = useState(false);

  const [eligibilityChecked, setEligibilityChecked] = useState(false);

  const [eligibility, setEligibility] = useState<{

    blocked: boolean;

    reason?: string;

    status?: string;

    multipleCustomers?: boolean;

    allowResubscribe?: boolean;

  }>({ blocked: false });

  const [canceling, setCanceling] = useState(false);

  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [foreverQuantities, setForeverQuantities] = useState<Record<string, number>>({});

  const [calculatorVisible, setCalculatorVisible] = useState(false);

const [calculatorContext, setCalculatorContext] = useState<SavingsCalculatorContext | null>(null);

const [calculatorLanguage, setCalculatorLanguage] = useState<string>(HUMAN_TRANSLATION_RATE_BANDS[0]?.key || '');

const [calculatorWordCount, setCalculatorWordCount] = useState<number>(5000);

const [sidePanelHovered, setSidePanelHovered] = useState(false);

const [testimonialIndex, setTestimonialIndex] = useState(0);
const [displayedWordsTranslated, setDisplayedWordsTranslated] = useState(0);
const wordsTickerBaseRef = useRef(0);
const wordsPerSecondRef = useRef(DEFAULT_WORDS_PER_SECOND);
const lastWordsFetchRef = useRef<{ total: number; timestamp: number } | null>(null);

const [referralStatus, setReferralStatus] = useState<ReferralStatusResponse | null>(null);
const [referralStatusLoading, setReferralStatusLoading] = useState(false);
const [referralModalVisible, setReferralModalVisible] = useState(false);
const [referralCodeInput, setReferralCodeInput] = useState('');
const [referralApplyLoading, setReferralApplyLoading] = useState(false);
const [pendingReferralPlan, setPendingReferralPlan] = useState<Plan | null>(null);


const isFuture = (unix?: number) => typeof unix === 'number' && unix * 1000 > Date.now();

const isCancelledButFuture = (d: any) =>

  String(d?.status || '').toLowerCase().startsWith('cancel') && isFuture(d?.cancel_at);


const fetchReferralStatus = useCallback(async () => {
  if (!isAuth || !userID) {
    setReferralStatus(null);
    return;
  }
  setReferralStatusLoading(true);
  try {
    const { data } = await axios.get<ReferralStatusResponse>(`${API_BASE}/referrals/status`, {
      params: { userID },
    });
    setReferralStatus(data);
  } catch (error) {
    setReferralStatus(null);
  } finally {
    setReferralStatusLoading(false);
  }
}, [isAuth, userID]);

useEffect(() => {
  if (!isAuth) {
    setReferralStatus(null);
    setReferralStatusLoading(false);
    return;
  }
  fetchReferralStatus();
}, [isAuth, fetchReferralStatus]);



  // Check if user has an active subscription (based on price ID), excluding users flagged for resubscribe

  const normalizedCurrentPlanPriceId = useMemo(
    () => ensureString(currentPlanPriceId, '').trim(),
    [currentPlanPriceId]
  );

  const hasActiveSubscription =
    !eligibility.allowResubscribe && !!normalizedCurrentPlanPriceId;

  const showTestimonialSnippet = !hasActiveSubscription;
  const shouldShowPromoStrip = !isForeverCredits;
  const currentTestimonial = TESTIMONIALS.length
    ? TESTIMONIALS[testimonialIndex % TESTIMONIALS.length]
    : null;
  const testimonialSnippet = currentTestimonial ? truncateQuote(currentTestimonial.quote, (shouldShowPromoStrip && showTestimonialSnippet) ? 500 : 500) : '';
  const testimonialAuthor = currentTestimonial?.author || '';
  const wordsTickerReady = displayedWordsTranslated > 0;
  const formattedWordsTranslated = wordsTickerReady
    ? formatNumber(Math.max(0, Math.floor(displayedWordsTranslated)))
    : 'Loading...';

  const shouldPromptAffiliate = useMemo(() => {
    if (!isAuth || isForeverCredits) return false;
    if (normalizedCurrentPlanPriceId) return false;
    if (referralStatusLoading) return false;
    if (!referralStatus) return false;
    if (referralStatus.appliedCode) return false;
    if (referralStatus.referralRewardChecked) return false;
    return referralStatus.eligibility === 'eligible' && referralStatus.canApply;
  }, [isAuth, isForeverCredits, normalizedCurrentPlanPriceId, referralStatus, referralStatusLoading]);

  const subscriberEligible = isForeverCredits && hasActiveSubscription;
  const normalizedCurrentPlanDiscount = ensureNumber(currentPlanForeverDiscountPercent, 0);

  const canPurchase = isForeverCredits || !hasActiveSubscription;

  const selectedCalculatorRate = useMemo(
    () =>
      HUMAN_TRANSLATION_RATE_BANDS.find((band) => band.key === calculatorLanguage) ||
      HUMAN_TRANSLATION_RATE_BANDS[0],
    [calculatorLanguage]
  );

  const calculatorHumanRateLow = selectedCalculatorRate?.low ?? 0;
  const calculatorHumanRateHigh = selectedCalculatorRate?.high ?? 0;

  const calculatorPlanCostPerWord = calculatorContext && calculatorContext.planCredits > 0 && calculatorContext.planPrice > 0
    ? calculatorContext.planPrice / calculatorContext.planCredits
    : 0;

  const calculatorHumanLow = selectedCalculatorRate
    ? calculatorWordCount * selectedCalculatorRate.low
    : 0;

  const calculatorHumanHigh = selectedCalculatorRate
    ? calculatorWordCount * selectedCalculatorRate.high
    : 0;

  const calculatorPlanCostEstimate = calculatorPlanCostPerWord * calculatorWordCount;

  const calculatorSavingsLow = Math.max(0, calculatorHumanLow - calculatorPlanCostEstimate);

  const calculatorSavingsHigh = Math.max(0, calculatorHumanHigh - calculatorPlanCostEstimate);

  const calculatorHasPlanData = Boolean(calculatorContext && calculatorContext.planCredits > 0 && calculatorContext.planPrice > 0);



  useEffect(() => {

    fetchPlans();

  }, []);

  

  useEffect(() => {

  const run = async () => {

    await fbPixel.waitForFbPixel();

    if (email || userID) {

      await fbPixel.init(email, userID);

    }

  };

  run();

}, [email, userID]);






  useEffect(() => {
    if (!showTestimonialSnippet || TESTIMONIALS.length <= 1) {
      return;
    }
    const rotationTimer = window.setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, TESTIMONIAL_ROTATION_INTERVAL_MS);
    return () => window.clearInterval(rotationTimer);
  }, [showTestimonialSnippet]);

  useEffect(() => {
    if (!showTestimonialSnippet) {
      setTestimonialIndex(0);
    }
  }, [showTestimonialSnippet]);

  useEffect(() => {
    if (isForeverCredits) {
      return;
    }
    let cancelled = false;

    const fetchCounters = async () => {
      try {
        const { data } = await axios.get(WORDS_COUNTER_ENDPOINT);
        if (cancelled) return;
        const totalWords = Number(data?.total_words_translated ?? 0);
        if (Number.isFinite(totalWords) && totalWords >= 0) {
          const now = Date.now();
          const previous = lastWordsFetchRef.current;
          if (previous) {
            const deltaWords = totalWords - previous.total;
            const deltaSeconds = Math.max((now - previous.timestamp) / 1000, 1);
            if (deltaWords > 0) {
              const computedRate = deltaWords / deltaSeconds;
              if (Number.isFinite(computedRate) && computedRate > 0) {
                wordsPerSecondRef.current = Math.max(computedRate, MIN_WORDS_PER_SECOND);
              }
            }
          } else if (totalWords > 0) {
            wordsPerSecondRef.current = Math.max(wordsPerSecondRef.current, MIN_WORDS_PER_SECOND);
          }
          lastWordsFetchRef.current = { total: totalWords, timestamp: now };
          wordsTickerBaseRef.current = Math.max(wordsTickerBaseRef.current, totalWords);
          setDisplayedWordsTranslated((prev) => {
            if (!Number.isFinite(prev) || prev < totalWords) {
              return totalWords;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to load usage counters', error);
      }
    };

    fetchCounters();
    const refreshTimer = window.setInterval(fetchCounters, WORDS_TICKER_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, [isForeverCredits]);

  useEffect(() => {
    if (isForeverCredits) {
      return;
    }
    let lastTick = Date.now();
    const tickInterval = window.setInterval(() => {
      const now = Date.now();
      const elapsedSeconds = Math.max((now - lastTick) / 1000, 0);
      lastTick = now;
      setDisplayedWordsTranslated((prev) => {
        const base = wordsTickerBaseRef.current;
        if (!Number.isFinite(base) || base <= 0) {
          return prev;
        }
        const previousValue = Number.isFinite(prev) ? prev : base;
        const next = previousValue + wordsPerSecondRef.current * elapsedSeconds;
        if (!Number.isFinite(next)) {
          return base;
        }
        return Math.max(next, base);
      });
    }, 1000);

    return () => window.clearInterval(tickInterval);
  }, [isForeverCredits]);

const fetchPlans = async () => {
  try {
    const allowAccountOnlyPlans = Boolean(
      includeSubscriberOnlyPlans && pricingSurface === 'account' && isAuth && !isForeverCredits && userID
    );
    const includeSubscriberOnly = allowAccountOnlyPlans;
    const headers = includeSubscriberOnly ? await getAuthHeaders() : undefined;
    const response = await axios.get(`${API_BASE}/get-subscription-plans`, {
      params: {
        includeInactive: false,
        includeLegacy: false,
        includeSubscriberOnly,
        userID: includeSubscriberOnly ? userID : undefined,
        surface: includeSubscriberOnly ? 'account' : undefined,
      },
      headers,
    });

    const subscriptionResponse: Plan[] = (response.data?.subscriptionPlans || [])
      .map((rawPlan: any) => normalizeSubscriptionPlan(rawPlan))
      .filter((plan: Plan) => Boolean(plan.planKey) && !plan.isLegacy && !isOnBreakPlan(plan));

    const foreverResponse: Plan[] = (response.data?.foreverCreditPlans || [])
      .map((rawPlan: any) => normalizeForeverPlan(rawPlan))
      .filter((plan: Plan) => Boolean(plan.planKey) && !plan.isLegacy && !isOnBreakPlan(plan));

    setSubscriptionPlansLookup(subscriptionResponse);

    if (isForeverCredits) {
      setPlans(foreverResponse.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } else {
      setPlans(subscriptionResponse);
    }
  } catch (error) {
    console.error('Failed to fetch plans:', error);
    message.error('Failed to load pricing plans');
  } finally {
    setLoading(false);
  }
};



const getFacebookTrackingParams = () => {

  const { fbp, fbc } = getFacebookCookies();

  return {

    fbp,

    fbc,

    clientUserAgent: navigator.userAgent,

    eventSourceUrl: window.location.href,

  };

};



// Pre-check eligibility for plan changes on load

useEffect(() => {

  if (isForeverCredits || !isAuth) return;

  let cancelled = false;

  (async () => {

    try {

      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_BASE}/get-subscription-details`, { params: { userID }, headers });

      if (cancelled) return;



      if (data.multiple_customers) {

        setEligibility({ blocked: true, multipleCustomers: true,

          reason: data.message || 'Multiple renewable subscriptions found. Please select one from Account ? Manage Subscription.',

          status: 'multiple' });

        return;

      }



      // Scheduled cancel still counts as 'has a subscription' (but block changes until reactivated).

      if (isCancelledButFuture(data)) {

        setEligibility({ blocked: true,

          reason: data.message || 'Your subscription is scheduled to cancel. Re-enable it from Manage Subscription.',

          status: 'cancelled' });

        return;

      }



      if (String(data.status || '').toLowerCase().startsWith('cancel')) {

        // Fully cancelled (no future cancel_at)

        setEligibility({ blocked: false, allowResubscribe: true,

          reason: data.message || 'Your subscription has been cancelled.',

          status: 'cancelled' });

        return;

      }



      setEligibility({ blocked: false, status: data.status });

    } catch (e: any) {

      const detail: string | undefined = e?.response?.data?.detail;

      const d = (detail || '').toLowerCase();



      // If BE says 'no renewable subscription', double-check for a scheduled cancel

      // so we don't falsely flip to 'No active subscription'.

      if (d.includes('no renewable subscription')) {

        try {

          const subs = (await axios.get(`${API_BASE}/get-user-subscriptions`, { params: { userID } }))?.data?.subscriptions || [];

          const sched = subs.find((s: any) =>

            (String(s?.status || '').toLowerCase().startsWith('cancel')) && isFuture(s?.cancel_at));

          if (sched) {

            setEligibility({ blocked: true,

              reason: 'Your subscription is scheduled to cancel. Re-enable it from Manage Subscription.',

              status: 'cancelled' });

          } else {

            setEligibility({ blocked: false, allowResubscribe: true,

              reason: 'You do not have an active subscription.',

              status: 'none' });

          }

        } catch {

          setEligibility({ blocked: false, allowResubscribe: true,

            reason: 'You do not have an active subscription.',

            status: 'none' });

        }

      } else if (d.includes('does not have a stripe customer id') || d.includes('no subscriptions found')) {

        setEligibility({ blocked: false, allowResubscribe: true,

          reason: 'No subscriptions found for this account.',

          status: 'none' });

      } else {

        setEligibility({ blocked: true, reason: detail || 'Failed to check subscription status.', status: 'unknown' });

      }

    } finally {

      if (!cancelled) setEligibilityChecked(true);

    }

  })();

  return () => { cancelled = true; };

}, [userID, isAuth, isForeverCredits]);



// Opens the change plan modal and fetches subscription details for dates

const openChangePlanModal = async (plan: Plan, action: 'upgrade' | 'downgrade') => {

  setChangeModalPlan(plan);

  setChangeAction(action);

  setSelectedTiming(action === 'upgrade' ? 'now' : 'next_cycle');

  setChangeModalVisible(true);

  setModalError(null);

  setModalBlocked(false);

  setLoadingDetails(true);

  try {

    const headers = await getAuthHeaders();

    const resp = await axios.get(`${API_BASE}/get-subscription-details`, {

      params: { userID },

      headers

    });

    const data = resp.data || {};



    // Handle known edge cases similar to Renew Subscription UX

if (data.multiple_customers) {

  setSubscriptionDetails(data);

  setModalError(data.message || 'Multiple renewable subscriptions found. Please select one from Account ? Manage Subscription.');

  setModalBlocked(true);

} else if (isCancelledButFuture(data)) {

  setSubscriptionDetails(data);

  setModalError(data.message || 'Your subscription is scheduled to cancel. Use Manage Subscription to re-enable before changing plans.');

  setModalBlocked(true);

} else if (String(data.status || '').toLowerCase().startsWith('cancel')) {

  setSubscriptionDetails(data);

  setModalError(data.message || 'Your subscription has been cancelled. Subscribe again to change plans.');

  setModalBlocked(true);

} else {

  setSubscriptionDetails(data);

  setModalBlocked(false);

}

  } catch (e: any) {

    console.error('Failed to load subscription details', e);

    const detail: string | undefined = e?.response?.data?.detail;

    // Map backend details to user-friendly messages

    let friendly = 'Failed to load subscription details';

    if (typeof detail === 'string') {

      const d = detail.toLowerCase();

      if (d.includes('no renewable subscription')) {

        friendly = 'No renewable subscription found for this account. Please subscribe or re-enable your subscription in Account ? Manage Subscription.';

      } else if (d.includes('does not have a stripe customer id')) {

        friendly = 'No Stripe customer found for this account. Please subscribe first from the pricing page.';

      } else if (d.includes('user not found')) {

        friendly = 'User not found. Please refresh the page or sign in again.';

      } else if (d.includes('no subscriptions found')) {

        friendly = 'No subscriptions found for this account.';

      } else {

        friendly = detail;

      }

    }

    setModalError(friendly);

    setModalBlocked(true);

    message.error(friendly);

  } finally {

    setLoadingDetails(false);

  }

};



const formatDate = (epoch?: number) => {

  if (!epoch) return 'N/A';

  try {

    return new Date(epoch * 1000).toLocaleString();

  } catch {

    return 'N/A';

  }

};



const formatDateOnly = (epoch?: number) => {

  if (!epoch) return 'N/A';

  try {

    return new Date(epoch * 1000).toLocaleDateString();

  } catch {

    return 'N/A';

  }

};





const submitChangePlan = async () => {

  if (!changeModalPlan || !changeAction) return;

  if (changeAction === 'downgrade' && selectedTiming === 'now') {

    return; // safeguarded by disabled UI

  }



  // Helper to redirect user to Stripe Customer Portal to update payment method

  const redirectToPortal = async () => {

    try {

      const resp = await axios.post(`${API_BASE}/create-customer-portal-session`, { userID });

      const url = resp.data?.url;

      if (url) {

        window.location.href = url;

      } else {

        window.location.href = '/account';

      }

    } catch {

      window.location.href = '/account';

    }

  };



  const executeChange = async () => {

    setSubmittingChange(true);

    setModalError(null);

    try {

      const idempotencyKey = `${userID}-${changeModalPlan.stripePriceID}-${Date.now()}`;
      const headers = await getAuthHeaders();

      const resp = await axios.post(`${API_BASE}/change-subscription-plan`, {

        userID,

        timing: selectedTiming,

        targetPriceId: changeModalPlan.stripePriceID,

        idempotencyKey

      }, {

        headers

      });



      // If backend requires user to select among multiple Stripe customers, redirect to Account

      if (resp.data?.multiple_customers) {

        message.info(resp.data?.message || 'Multiple subscriptions found. Redirecting to Account to manage.');

        setChangeModalVisible(false);

        try {

          window.location.href = '/account';

        } catch {

          // no-op

        }

        return;

      }



      const data = resp.data || {};
      const serverMessage = ensureString(data?.message, '');
      const invoiceId = data?.invoice_id || data?.invoiceId;
      const invoiceStatus = data?.invoice_status || data?.invoiceStatus;
      const hostedInvoiceUrl = data?.hosted_invoice_url || data?.hostedInvoiceUrl;
      const invoicePdf = data?.invoice_pdf || data?.invoicePdf;

      if (invoiceId && invoiceStatus !== 'paid') {
        const notice = serverMessage || 'Plan change invoice created. Please complete payment to finalize the change.';
        message.info(notice);
        Modal.info({
          title: 'Manual Payment Required',
          content: (
            <Space direction="vertical">
              <Text>{notice}</Text>
              {hostedInvoiceUrl ? (
                <Button type="primary" href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  View Invoice
                </Button>
              ) : null}
              {!hostedInvoiceUrl && invoicePdf ? (
                <Button type="primary" href={invoicePdf} target="_blank" rel="noopener noreferrer">
                  Download Invoice PDF
                </Button>
              ) : null}
            </Space>
          ),
          okText: 'Close',
        });
      } else {
        message.success(serverMessage || 'Plan change submitted');
      }

      setChangeModalVisible(false);

      if (onSuccess) onSuccess();

    } catch (err: any) {

      const detail = err?.response?.data?.detail;



      // Handle SCA/3DS required for subscription change (upgrade now)

      if (detail && typeof detail === 'object' && detail.payment_intent_client_secret) {

        try {

          const stripe = await getStripePromise();

          if (!stripe) {

            message.error('Payment service is unavailable. Please try again later');

          } else {

            const result = await stripe.confirmCardPayment(detail.payment_intent_client_secret);

            if (result.error) {

              message.error(result.error.message || 'Payment authentication failed. Please try again.');

            } else {

              message.success('Plan changed and renewed successfully');

              setChangeModalVisible(false);

              if (onSuccess) onSuccess();

            }

          }

        } catch {

          message.error('Payment authentication failed. Please try again.');

        }

        return;

      }



      // Redirect to customer portal for payment method issues

      if ((detail && typeof detail === 'object' && detail.requires_payment_method) ||

          (typeof detail === 'string' && (() => {

            const d = detail.toLowerCase();

            return d.includes('requires_payment_method') || d.includes('no default payment method') || d.includes('default_payment_method');

          })())) {

        await redirectToPortal();

        return;

      }



      if (typeof detail === 'string') {

        setModalError(detail);

        message.error(detail);

      } else if (detail?.error) {

        setModalError(detail.error);

        message.error(detail.error);

      } else {

        setModalError('Failed to change plan');

        message.error('Failed to change plan');

      }

    } finally {

      setSubmittingChange(false);

    }

  };



  // Near-renewal safeguard: confirm before charging immediately

  if (changeAction === 'upgrade' && selectedTiming === 'now' && subscriptionDetails?.renewal_proximity_warning) {

    Modal.confirm({

      title: 'Renewal is imminent',

      content: `${subscriptionDetails.renewal_proximity_warning} Proceed with an immediate upgrade and charge now?`,

      okText: 'Proceed and charge now',

      cancelText: 'Cancel',

      onOk: executeChange,

    });

    return;

  }



  await executeChange();

};



  const cancelScheduledChange = async () => {

    setCanceling(true);

    try {

      const headers = await getAuthHeaders();

      const resp = await axios.post(`${API_BASE}/cancel-scheduled-plan-change`, { userID }, { headers });

      if (resp.data?.multiple_customers) {

        message.info('Multiple subscriptions found. Redirecting to Account to manage.');

        try {

          window.location.href = '/account';

        } catch {

          // no-op

        }

        return;

      }

      message.success(resp.data?.message || 'Scheduled change canceled');

      if (onSuccess) onSuccess();

    } catch (err: any) {

      const detail = err?.response?.data?.detail || 'Failed to cancel scheduled change';

      message.error(detail);

    } finally {

      setCanceling(false);

    }

  };



  const openSavingsCalculator = (context: SavingsCalculatorContext) => {

    setCalculatorContext(context);

    const normalizedCredits = Number.isFinite(context.planCredits) ? Math.max(context.planCredits, 0) : 0;

    const suggestedWordCount = normalizedCredits > 0
      ? Math.min(Math.round(normalizedCredits), 50000)
      : 5000;

    setCalculatorWordCount(suggestedWordCount > 0 ? suggestedWordCount : 5000);

    setCalculatorVisible(true);

  };



  const closeSavingsCalculator = () => {

    setCalculatorVisible(false);

    setCalculatorContext(null);

  };

  const handleForeverQuantityChange = useCallback(
    (quantityKey: string, planRef: Plan, planId: string, value: number | null) => {
      const next = clampForeverQuantity(value);
      setForeverQuantities((prev) => {
        if (prev[quantityKey] === next) {
          return prev;
        }
        return { ...prev, [quantityKey]: next };
      });
      if (onForeverQuantityChange) {
        onForeverQuantityChange({
          planId,
          plan: planRef,
          quantity: next,
        });
      }
    },
    [onForeverQuantityChange]
  );

  const resetForeverQuantity = useCallback((quantityKey: string) => {
    setForeverQuantities((prev) => {
      if (prev[quantityKey] === undefined || prev[quantityKey] === 1) {
        return prev;
      }
      const next = { ...prev };
      next[quantityKey] = 1;
      return next;
    });
  }, []);



  const handleDirectForeverPurchase = async (plan: Plan, quantityKey: string, rawQuantity?: number) => {

    if (!isAuth && showLoginModal) {

      showLoginModal();

      return;

    }

    if (isProcessing) return;

    const quantity = clampForeverQuantity(rawQuantity);

    setIsProcessing(true);

    try {

      const resp = await axios.post(`${API_BASE}/purchase-forever-credits-direct`, {

        userID,

        priceId: plan.stripePriceID,

        quantity,

      });

      if (resp?.data?.pending) {
        const pendingMessage =
          typeof resp.data?.message === 'string' && resp.data.message
            ? resp.data.message
            : 'Payment is processing. Credits will be added automatically once Stripe confirms the invoice.';
        message.info(pendingMessage);
        return;
      }

      message.success('Forever credits purchased successfully');
      resetForeverQuantity(quantityKey);

      if (onSuccess) onSuccess();

    } catch (err: any) {

      const detail = err?.response?.data?.detail;

      const detailString: string =
        typeof detail === 'string'
          ? detail
          : typeof detail?.message === 'string'
          ? detail.message
          : typeof detail?.error === 'string'
          ? detail.error
          : '';

      if (detailString && detailString.toLowerCase().includes('stripe customer id')) {
        await handleCheckout(plan);
        return;
      }

      const clientSecret: string | undefined =

        typeof detail === 'object' && detail ? detail.payment_intent_client_secret : undefined;

      const invoiceId: string | undefined =

        typeof detail === 'object' && detail ? detail.invoice_id : undefined;



      // If backend indicates no default payment method, fall back to Checkout to collect a card

      const requiresPM: boolean = typeof detail === 'object' && !!detail?.requires_payment_method;

      if (requiresPM) {

        await handleCheckout(plan);

        return;

      }



      // New: handle string-form errors from backend indicating no default payment method

      if (typeof detail === 'string') {

        const d = detail.toLowerCase();

        const needsPM =

          d.includes('default_payment_method') ||

          d.includes('no default payment method') ||

          d.includes('requires a payment method') ||

          (d.includes('payment failed') && d.includes('default_payment_method'));

        if (needsPM) {

          await handleCheckout(plan);

          return;

        }

      }



      if (clientSecret) {

        const stripe = await getStripePromise();

        if (!stripe) {

          message.error('Payment service is unavailable. Please try again later');

        } else {

          const result = await stripe.confirmCardPayment(clientSecret);

          if (result.error) {

            message.error(result.error.message || 'Payment authentication failed. Please try again.');

          } else {

            try {

              if (invoiceId) {

                await axios.post(`${API_BASE}/confirm-forever-credits-payment`, {

                  userID,

                  invoice_id: invoiceId,

                });

              }

            } catch {

              message.error(
                invoiceId
                  ? `Payment succeeded, but we couldn't finalize your credits. Contact support with invoice ID ${invoiceId}.`
                  : "Payment succeeded, but we couldn't finalize your credits. Please contact support."
              );
              return;

            }

            message.success('Forever credits purchased successfully');
            resetForeverQuantity(quantityKey);

            if (onSuccess) onSuccess();

          }

        }

      } else {

        message.error(err?.response?.data?.detail || 'Failed to complete purchase');

      }

    } finally {

      setIsProcessing(false);

    }

  };



  const startStripeCheckout = useCallback(async (plan: Plan) => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const quantityKey = resolveForeverQuantityKey(plan);
      const initialQuantity = isForeverCredits ? clampForeverQuantity(foreverQuantities[quantityKey] ?? 1) : 1;
      let checkoutQuantity = initialQuantity;
      const initiateCheckoutEventId = fbPixel.generateEventId('InitiateCheckout');
      const anonymousFunnelId = generateRevenueFunnelId();

      await fbPixel.waitForFbPixel();
      fbPixel.event(
        'InitiateCheckout',
        {
          value: plan.price * checkoutQuantity,
          currency: 'USD',
          content_ids: [plan.planName],
          content_type: isForeverCredits ? 'product' : 'subscription',
        },
        initiateCheckoutEventId
      );

      const trackingParams = getFacebookTrackingParams();

      let successUrl = `${window.location.origin}/account`;
      if (!hasActiveSubscription && !isForeverCredits) {
        successUrl = `${window.location.origin}/checkout-success`;
      }

      const checkoutData: CheckoutSessionRequest = {
        priceId: plan.stripePriceID,
        userId: userID,
        email: email,
        successUrl,
        cancelUrl: window.location.href,
        isForeverCredits,
        quantity: checkoutQuantity,
        metadata: {
          plan: plan.planName,
          planKey: plan.planKey,
          billingInterval: plan.billingInterval,
          intervalMonths: plan.intervalMonths,
          creditsPerInterval: plan.creditsPerInterval,
          monthlyEquivalentCredits: plan.monthlyEquivalentCredits ?? plan.monthlyCredits,
          productId: plan.stripeProductID,
          currency: plan.currency,
          initiateCheckoutEventId,
          anonymousFunnelId,
          isForeverCredits,
          subscriberDiscountEligible: subscriberEligible,
        },
        ...trackingParams,
      };

      if (isForeverCredits) {
        checkoutData.metadata = checkoutData.metadata || {};
        checkoutData.metadata.forever_quantity = String(checkoutQuantity);
      }

      const hookContext: BeforeCheckoutContext = {
        quantity: checkoutQuantity,
        planId: plan.id || plan.stripePriceID || plan.planName || quantityKey,
        isForeverCredits,
        priceId: plan.stripePriceID,
      };

      if (beforeCheckout) {
        const hookResult = await beforeCheckout(plan, hookContext);
        if (hookResult === false) {
          return;
        }
        if (hookResult && typeof hookResult === 'object') {
          if (hookResult.metadata) {
            checkoutData.metadata = {
              ...checkoutData.metadata,
              ...hookResult.metadata,
            };
          }
          if (hookResult.payload) {
            Object.assign(checkoutData, hookResult.payload);
          }
        }
      }

      if (isForeverCredits) {
        const normalizedQuantity = clampForeverQuantity(
          typeof checkoutData.quantity === 'number' ? checkoutData.quantity : checkoutQuantity
        );
        checkoutQuantity = normalizedQuantity;
        checkoutData.quantity = normalizedQuantity;
        checkoutData.metadata = checkoutData.metadata || {};
        checkoutData.metadata.forever_quantity = String(normalizedQuantity);
        if (checkoutData.isGiftPurchase) {
          checkoutData.metadata.gift_quantity = String(normalizedQuantity);
        }
      }

      try {
        const source =
          typeof checkoutData.metadata?.source === 'string' && checkoutData.metadata.source
            ? checkoutData.metadata.source
            : isForeverCredits
            ? 'custom_pricing_forever'
            : 'custom_pricing_subscription'
        await trackRevenuePricingIntent({
          anonymousFunnelId,
          planKey: plan.planKey || plan.planName || plan.stripePriceID,
          source,
          surface: isForeverCredits ? 'custom_pricing_forever' : 'custom_pricing',
          isForeverCredits,
        })
      } catch (intentError) {
        console.warn('Failed to record revenue pricing intent', intentError)
      }

      const response = await axios.post(`${API_BASE}/create-checkout-session`, checkoutData);

      const stripe = await getStripePromise();
      if (!stripe) {
        console.error('Stripe.js failed to initialize');
        message.error('Payment service is unavailable. Please try again later');
        return;
      }

      const { error } = await stripe.redirectToCheckout({ sessionId: response.data.id });

      if (error) {
        console.error('Stripe redirect error:', error);
        message.error('Failed to redirect to checkout');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      message.error('Failed to start checkout process');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, isForeverCredits, normalizedCurrentPlanPriceId, subscriberEligible, userID, email, beforeCheckout, foreverQuantities]);

  const handleCheckout = useCallback((plan: Plan) => {
    if (!isAuth && showLoginModal) {
      showLoginModal();
      return;
    }

    if (shouldPromptAffiliate) {
      setPendingReferralPlan(plan);
      let initialCode = '';
      if (typeof window !== 'undefined') {
        try {
          const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
          if (stored) {
            initialCode = sanitizeAffiliateCode(stored);
          }
        } catch (error) {
          // ignore storage errors
        }
      }
      setReferralCodeInput(initialCode);
      setReferralModalVisible(true);
      return;
    }

    startStripeCheckout(plan);
  }, [isAuth, showLoginModal, shouldPromptAffiliate, startStripeCheckout]);

  const handleReferralCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReferralCodeInput(sanitizeAffiliateCode(event.target.value || ''));
  };

  const handleReferralSkip = useCallback(() => {
    if (!pendingReferralPlan) {
      setReferralModalVisible(false);
      setReferralCodeInput('');
      setReferralApplyLoading(false);
      return;
    }
    const planToCheckout = pendingReferralPlan;
    setReferralModalVisible(false);
    setPendingReferralPlan(null);
    setReferralCodeInput('');
    setReferralApplyLoading(false);
    startStripeCheckout(planToCheckout);
  }, [pendingReferralPlan, startStripeCheckout]);

  const handleReferralApply = useCallback(async () => {
    if (!pendingReferralPlan) {
      setReferralModalVisible(false);
      return;
    }
    const planToCheckout = pendingReferralPlan;
    const trimmed = sanitizeAffiliateCode(referralCodeInput);
    if (!trimmed) {
      message.error('Enter an affiliate code or choose Skip.');
      return;
    }
    setReferralApplyLoading(true);
    try {
      await axios.post(`${API_BASE}/referrals/apply`, {
        userID,
        referralCode: trimmed,
      });
      message.success('Affiliate code saved to your account. Credits unlock after your first payment.');
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
        } catch (error) {
          // ignore storage errors
        }
      }
      await fetchReferralStatus();
      setReferralModalVisible(false);
      setPendingReferralPlan(null);
      setReferralCodeInput('');
      startStripeCheckout(planToCheckout);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        const detail = String((error.response.data as { detail?: string })?.detail || '');
        const detailLower = detail.toLowerCase();
        if (status === 404) {
          message.error('Affiliate code not found.');
        } else if (status === 409) {
          message.info(detail || 'An affiliate code is already linked to this account.');
          await fetchReferralStatus();
          setReferralModalVisible(false);
          setPendingReferralPlan(null);
          setReferralCodeInput('');
          startStripeCheckout(planToCheckout);
          return;
        } else if (status === 400 && detailLower.includes('before completing a purchase')) {
          message.info('Affiliate codes must be applied before your first purchase.');
          await fetchReferralStatus();
          setReferralModalVisible(false);
          setPendingReferralPlan(null);
          setReferralCodeInput('');
          startStripeCheckout(planToCheckout);
          return;
        } else if (detail) {
          message.error(detail);
        } else {
          message.error('Failed to apply affiliate code. Please try again.');
        }
      } else {
        message.error('Failed to apply affiliate code. Please try again.');
      }
    } finally {
      setReferralApplyLoading(false);
    }
  }, [pendingReferralPlan, referralCodeInput, userID, fetchReferralStatus, startStripeCheckout]);


  const waitingForEligibility = !isForeverCredits && isAuth && !eligibilityChecked;
  const subscriptionPlanGroups = useMemo<SubscriptionPlanGroup[]>(() => {
    if (isForeverCredits) {
      return [];
    }
    return buildSubscriptionPlanGroups(plans);
  }, [isForeverCredits, plans]);

  const metadataSubscriptionPlanGroups = useMemo<SubscriptionPlanGroup[]>(
    () => buildSubscriptionPlanGroups(subscriptionPlansLookup),
    [subscriptionPlansLookup]
  );

  const orderedSubscriptionMetadata = useMemo(
    () => metadataSubscriptionPlanGroups.map((group) => group.metadata),
    [metadataSubscriptionPlanGroups]
  );

  const priceIdToMetadata = useMemo(() => {
    const map = new Map<string, SubscriptionPlanMetadata>();
    metadataSubscriptionPlanGroups.forEach((group) => {
      if (group.monthly?.stripePriceID) {
        map.set(group.monthly.stripePriceID, group.metadata);
      }
      if (group.yearly?.stripePriceID) {
        map.set(group.yearly.stripePriceID, group.metadata);
      }
    });
    return map;
  }, [metadataSubscriptionPlanGroups]);

  const currentPlanMetadata = useMemo(
    () => (normalizedCurrentPlanPriceId ? priceIdToMetadata.get(normalizedCurrentPlanPriceId) : undefined),
    [priceIdToMetadata, normalizedCurrentPlanPriceId]
  );

  const currentPlanLookup = useMemo(() => {
    if (!normalizedCurrentPlanPriceId) {
      return undefined;
    }
    const matchFromLookup = subscriptionPlansLookup.find(
      (plan) => plan.stripePriceID === normalizedCurrentPlanPriceId
    );
    if (matchFromLookup) {
      return matchFromLookup;
    }
    return plans.find((plan) => plan.stripePriceID === normalizedCurrentPlanPriceId);
  }, [normalizedCurrentPlanPriceId, subscriptionPlansLookup, plans]);

  const currentPlanOrder = useMemo(() => {
    if (currentPlanMetadata && Number.isFinite(currentPlanMetadata.order)) {
      return currentPlanMetadata.order;
    }
    if (currentPlanLookup && typeof currentPlanLookup.sortOrder === 'number') {
      return currentPlanLookup.sortOrder;
    }
    return 0;
  }, [currentPlanMetadata, currentPlanLookup]);

  const isLegacyCurrentPlan = useMemo(() => {
    if (currentPlanIsLegacy) {
      return true;
    }
    if (isForeverCredits) {
      return false;
    }
    if (!normalizedCurrentPlanPriceId) {
      return false;
    }
    if (currentPlanLookup) {
      return ensureBoolean(currentPlanLookup.isLegacy, false);
    }
    const meta = priceIdToMetadata.get(normalizedCurrentPlanPriceId);
    return ensureBoolean(meta?.isLegacy, false);
  }, [currentPlanIsLegacy, isForeverCredits, normalizedCurrentPlanPriceId, currentPlanLookup, priceIdToMetadata]);

const displayPlans = useMemo(() => {
  if (isForeverCredits) {
    return plans
      .map((plan) => {
        const meta = ensureMetadata(plan.metadata);
        const displayName = ensureString(pickDefined(plan.displayName, plan.planName, meta.displayName), plan.planName);
        const order = ensureNumber(pickDefined(plan.sortOrder, meta.order), 0);
        const credits = ensureNumber(pickDefined(plan.oneTimeCredits, meta.credits, meta.oneTimeCredits), plan.oneTimeCredits ?? 0);
        const decorated = { ...plan } as Plan;
        (decorated as any).__metadata = {
          displayName,
          order,
          credits,
          price: decorated.price,
          badgeText: meta.badgeText,
          badgeColor: meta.badgeColor,
          highlight: ensureBoolean(meta.highlight, false),
        };
        return decorated;
      })
      .sort((a, b) => {
        const metaA = (a as any).__metadata as { order?: number; credits?: number };
        const metaB = (b as any).__metadata as { order?: number; credits?: number };
        const orderDiff = (metaA.order ?? 0) - (metaB.order ?? 0);
        if (orderDiff !== 0) {
          return orderDiff;
        }
        const creditsDiff = (metaA.credits ?? 0) - (metaB.credits ?? 0);
        if (creditsDiff !== 0) {
          return creditsDiff;
        }
        return (a.price ?? 0) - (b.price ?? 0);
      });
  }

  return subscriptionPlanGroups
    .map((group) => {
      const interval = billingInterval;
      const primary = interval === 'yearly' ? group.yearly : group.monthly;
      const fallback = interval === 'yearly' ? group.monthly : group.yearly;
      const basePlan = primary ?? fallback;
      if (!basePlan) {
        return null;
      }
      const unavailableLabel = interval === 'yearly' ? 'Annual plan coming soon' : 'Monthly plan coming soon';
      const unavailableTooltip = interval === 'yearly' ? 'Annual plan is coming soon' : 'Monthly plan is coming soon';

      const cloned = { ...basePlan } as Plan;
      const referencePlan = primary ?? fallback;
      const monthlyCreditsForInterval = interval === 'yearly'
        ? ensureNumber(
            group.metadata.monthlyCredits,
            computeMonthlyCreditsFromPlan(referencePlan) ?? 0
          )
        : ensureNumber(
            group.metadata.monthlyCredits,
            computeMonthlyCreditsFromPlan(referencePlan) ?? referencePlan?.monthlyCredits ?? 0
          );

      cloned.monthlyCredits = monthlyCreditsForInterval;
      (cloned as any).__metadata = group.metadata;
      (cloned as any).__interval = interval;
      (cloned as any).__planAvailable = Boolean(primary);
      (cloned as any).__fallback = primary ? null : (fallback ?? null);
      const primaryCreditsPerInterval = primary?.creditsPerInterval;
      const fallbackCreditsPerInterval = fallback?.creditsPerInterval;
      (cloned as any).__creditsPerInterval = interval === 'yearly'
        ? primaryCreditsPerInterval
          ?? group.metadata.totalAnnualCredits
          ?? (fallbackCreditsPerInterval ?? monthlyCreditsForInterval * 12)
        : primaryCreditsPerInterval
          ?? group.metadata.monthlyCredits
          ?? fallbackCreditsPerInterval
          ?? monthlyCreditsForInterval;

      (cloned as any).__unavailableLabel = unavailableLabel;
      (cloned as any).__unavailableTooltip = unavailableTooltip;

      return cloned;
    })
    .filter((plan): plan is Plan => plan !== null);
}, [isForeverCredits, plans, subscriptionPlanGroups, billingInterval]);

  const isCompact = isForeverCredits && compactForeverGrid;
  const btnHeight = isCompact ? '36px' : '44px';
  const visiblePlans = useMemo(() => {
    const sortedPlans = [...displayPlans];
    return isForeverCredits && compactForeverGrid ? sortedPlans.slice(0, 2) : sortedPlans;
  }, [compactForeverGrid, displayPlans, isForeverCredits]);
  const normalizedShortfallCredits = useMemo(() => {
    const directShortfall = Number(shortfallContext?.shortfallCredits);
    if (Number.isFinite(directShortfall) && directShortfall > 0) {
      return Math.max(0, Math.ceil(directShortfall));
    }
    const required = Number(shortfallContext?.requiredCredits);
    const available = Number(shortfallContext?.availableCredits);
    if (Number.isFinite(required) && Number.isFinite(available)) {
      return Math.max(0, Math.ceil(required - available));
    }
    return 0;
  }, [
    shortfallContext?.availableCredits,
    shortfallContext?.requiredCredits,
    shortfallContext?.shortfallCredits,
  ]);
  const foreverRecommendationPlans = useMemo<ForeverPlanRecommendationInput[]>(() => {
    if (!isForeverCredits || visiblePlans.length === 0) {
      return [];
    }
    return visiblePlans
      .map((plan) => {
        const fallbackPlan = (plan as any).__fallback as Plan | null | undefined;
        const planAvailable = Boolean((plan as any).__planAvailable ?? true);
        const planForActions = (planAvailable ? plan : fallbackPlan) || plan;
        const foreverMeta = (plan as any).__metadata as ResolvedForeverPlanMetadata | undefined;
        const planDisplayName = foreverMeta?.displayName || plan.planName;
        const planId = plan.id || fallbackPlan?.id || `${planDisplayName}-forever`;
        const quantityKey = resolveForeverQuantityKey(planForActions, planId);
        const creditsPerBundle = ensureNumber(
          foreverMeta?.credits ?? plan.oneTimeCredits ?? plan.creditsPerInterval,
          0
        );
        const basePrice = ensureNumber(foreverMeta?.price ?? plan.price, 0);
        const subscriberDiscountPercent = subscriberEligible
          ? ensureNumber(
              pickDefined(
                currentPlanMetadata?.foreverCreditDiscountPercent,
                normalizedCurrentPlanDiscount
              ),
              normalizedCurrentPlanDiscount
            )
          : 0;
        const pricePerBundle =
          subscriberEligible && subscriberDiscountPercent > 0
            ? Number((basePrice * (1 - subscriberDiscountPercent / 100)).toFixed(2))
            : basePrice;
        return {
          planId: quantityKey,
          planName: planDisplayName,
          creditsPerBundle,
          pricePerBundle,
        };
      })
      .filter((plan) => plan.creditsPerBundle > 0 && plan.pricePerBundle >= 0);
  }, [
    currentPlanMetadata?.foreverCreditDiscountPercent,
    isForeverCredits,
    normalizedCurrentPlanDiscount,
    subscriberEligible,
    visiblePlans,
  ]);
  const topUpRecommendation = useMemo(() => {
    if (!isForeverCredits || normalizedShortfallCredits <= 0) {
      return null;
    }
    const renewalInput =
      sidePanel?.type === 'currentPlan'
        ? {
            enabled: !sidePanel?.renewDisabled,
            price: sidePanel?.monthlyPrice,
            billingInterval: sidePanel?.billingInterval,
            intervalMonths: sidePanel?.intervalMonths,
            monthlyCreditsAllocated: sidePanel?.monthlyCreditsAllocated,
            upfrontCredits: sidePanel?.upfrontCredits,
            label: 'Renew Plan Now',
          }
        : null;
    return chooseCreditTopUpRecommendation({
      shortfallCredits: normalizedShortfallCredits,
      renewal: renewalInput,
      foreverPlans: foreverRecommendationPlans,
      maxBundleQuantity: 10,
    });
  }, [
    foreverRecommendationPlans,
    isForeverCredits,
    normalizedShortfallCredits,
    sidePanel?.billingInterval,
    sidePanel?.intervalMonths,
    sidePanel?.monthlyCreditsAllocated,
    sidePanel?.monthlyPrice,
    sidePanel?.renewDisabled,
    sidePanel?.type,
    sidePanel?.upfrontCredits,
  ]);
  const recommendedTopUpCandidate = topUpRecommendation?.recommended ?? null;
  const shouldShowTopUpRecommendation = Boolean(
    isForeverCredits &&
      normalizedShortfallCredits > 0 &&
      recommendedTopUpCandidate
  );
  const topUpRecommendationMessage = useMemo(() => {
    if (!shouldShowTopUpRecommendation || !recommendedTopUpCandidate) {
      return '';
    }
    if (recommendedTopUpCandidate.kind === 'renewal') {
      return `Renew now for ${formatCurrency(recommendedTopUpCandidate.totalPrice)} to add ${formatNumber(
        recommendedTopUpCandidate.coveredCredits
      )} credits.`;
    }
    const bundleLabel = `${recommendedTopUpCandidate.quantity || 1} ${
      (recommendedTopUpCandidate.quantity || 1) === 1 ? 'bundle' : 'bundles'
    }`;
    return `Buy ${bundleLabel} of ${
      recommendedTopUpCandidate.planName || 'Forever credits'
    } for ${formatCurrency(recommendedTopUpCandidate.totalPrice)} total.`;
  }, [recommendedTopUpCandidate, shouldShowTopUpRecommendation]);

  useEffect(() => {
    if (
      !isForeverCredits ||
      !recommendedTopUpCandidate ||
      recommendedTopUpCandidate.kind !== 'forever' ||
      !recommendedTopUpCandidate.planId ||
      !recommendedTopUpCandidate.quantity
    ) {
      return;
    }
    const nextQuantity = clampForeverQuantity(recommendedTopUpCandidate.quantity);
    const recommendationKey = recommendedTopUpCandidate.planId;
    setForeverQuantities((prev) => {
      if (prev[recommendationKey] === nextQuantity) {
        return prev;
      }
      return { ...prev, [recommendationKey]: nextQuantity };
    });
  }, [isForeverCredits, recommendedTopUpCandidate]);

  if (loading || waitingForEligibility) {
    const loadingLabel = waitingForEligibility
      ? 'Checking eligibility...'
      : 'Loading pricing plans...';

    return (
      <div className="loading-card-wrapper" style={{ padding: '40px' }}>
        <LoadingCard label={loadingLabel} />
      </div>
    );
  }



// Pre-render cards so we can optionally return "cards only"

const renderBooleanIcon = (value: boolean) =>
  value ? (<CheckCircleOutlined style={{ color: accentSuccess, marginRight: 6 }} />) : (<CloseCircleOutlined style={{ color: accentDanger, marginRight: 6 }} />);

const renderPricingIntervalToggle = () => (
  !isForeverCredits ? (
    <div className="pricing-interval-toggle">
      <Radio.Group
        value={billingInterval}
        onChange={(e) => setBillingInterval(e.target.value)}
        buttonStyle="solid"
      >
        <Radio.Button value="monthly">Monthly</Radio.Button>
        <Radio.Button value="yearly">Annual (2 months free)</Radio.Button>
      </Radio.Group>
    </div>
  ) : null
);

const renderPricingActionBanner = () => (
  !isForeverCredits &&
  isAuth &&
  eligibilityChecked &&
  !normalizedCurrentPlanPriceId &&
  (eligibility.blocked || eligibility.allowResubscribe) ? (
    <div className={`pricing-action-banner${eligibility.blocked ? ' pricing-action-banner--warning' : ''}`}>
      <div>
        <Text strong className="pricing-action-banner-title">
          {eligibility.blocked ? 'Action required' : 'No active subscription'}
        </Text>
        <Text type="secondary" className="pricing-action-banner-copy">
          {eligibility.reason || (eligibility.allowResubscribe ? 'Select a plan below to start a new subscription.' : '')}
        </Text>
      </div>
      {eligibility.blocked && (
        <Button
          onClick={async () => {
            try {
              const resp = await axios.post(`${API_BASE}/create-customer-portal-session`, { userID });
              const url = resp.data?.url;
              if (url) {
                window.location.href = url;
              } else if (resp.data?.multiple_customers) {
                window.location.href = '/account';
              } else {
                window.location.href = '/account';
              }
            } catch {
              window.location.href = '/account';
            }
          }}
        >
          Manage Subscription
        </Button>
      )}
    </div>
  ) : null
);


const cards = visiblePlans.map((plan) => {
  const subscriptionMeta = !isForeverCredits
    ? ((plan as any).__metadata as SubscriptionPlanMetadata | undefined)
    : undefined;
  const foreverMeta = isForeverCredits
    ? ((plan as any).__metadata as ResolvedForeverPlanMetadata | undefined)
    : undefined;
  const interval: BillingInterval = (!isForeverCredits
    ? ((plan as any).__interval as BillingInterval | undefined)
    : 'monthly') || 'monthly';
  const planAvailable = Boolean((plan as any).__planAvailable ?? true);
  const fallbackPlan = !isForeverCredits ? ((plan as any).__fallback as Plan | null | undefined) : null;
  const planForActions = (planAvailable ? plan : fallbackPlan) || plan;
  const unavailableLabel =
    ((plan as any).__unavailableLabel as string | undefined) ||
    (interval === 'yearly' ? 'Annual plan coming soon' : 'Monthly plan coming soon');
  const unavailableTooltip =
    ((plan as any).__unavailableTooltip as string | undefined) ||
    (interval === 'yearly' ? 'Annual plan is coming soon' : 'Monthly plan is coming soon');
      const planIsLegacy = Boolean((planForActions as any)?.isLegacy ?? subscriptionMeta?.isLegacy);
      const planDisplayName = isForeverCredits
        ? foreverMeta?.displayName || plan.planName
        : subscriptionMeta?.displayName || plan.planName;
      const planId = plan.id || fallbackPlan?.id || `${planDisplayName}-${interval}`;
      const isHovered = hoveredCard === planId;
      const isPopular = !isForeverCredits && !!subscriptionMeta?.highlight && !isCompact;
      const effectiveActive = hasActiveSubscription;
      const blocked = eligibility.blocked;
      const planStripePriceId = plan.stripePriceID || fallbackPlan?.stripePriceID || planForActions.stripePriceID;
      const isPendingTarget = !!(pendingChange && planStripePriceId && pendingChange.targetPriceId === planStripePriceId);
      const scheduledDate = pendingChange?.effectiveAt ? formatDate(pendingChange.effectiveAt) : null;
      const planOrder =
        subscriptionMeta?.order ??
        ensureNumber((planForActions as any)?.sortOrder, plan.sortOrder ?? 0);
      const isCurrent =
        hasActiveSubscription && planStripePriceId === normalizedCurrentPlanPriceId;
      const isUpgradeOption = hasActiveSubscription
        ? (isLegacyCurrentPlan ? !planIsLegacy : planOrder > currentPlanOrder)
        : false;
      const isDowngradeOption = hasActiveSubscription
        ? (!isLegacyCurrentPlan && planOrder < currentPlanOrder)
        : false;
  const credits = isForeverCredits
    ? foreverMeta?.credits ?? Number(plan.oneTimeCredits || 0)
    : subscriptionMeta?.monthlyCredits ?? plan.monthlyCredits ?? 0;
  const upfrontCredits = !isForeverCredits && interval === 'yearly'
    ? Number((plan as any).__creditsPerInterval ?? plan.creditsPerInterval ?? 0)
    : undefined;
  const renderLabel = (text: string, buttonId?: string, icon?: React.ReactNode) => (
    <span>
      {text}
      {(buttonId ? hoveredButton === buttonId : isHovered) && icon}
    </span>
  );

  if (isForeverCredits) {
    const basePrice = foreverMeta?.price ?? plan.price;
    const subscriberDiscountPercent = subscriberEligible
      ? ensureNumber(
          pickDefined(
            currentPlanMetadata?.foreverCreditDiscountPercent,
            normalizedCurrentPlanDiscount
          ),
          normalizedCurrentPlanDiscount
        )
      : 0;
    const discountedPrice =
      subscriberDiscountPercent > 0
        ? Number((basePrice * (1 - subscriberDiscountPercent / 100)).toFixed(2))
        : basePrice;

    const foreverWordCount = Math.max(credits || 0, 0);
    const effectiveForeverPrice = subscriberEligible && subscriberDiscountPercent > 0
      ? discountedPrice
      : basePrice;
    const quantityKey = resolveForeverQuantityKey(planForActions, planId);
    const isRecommendedForever =
      shouldShowTopUpRecommendation &&
      recommendedTopUpCandidate?.kind === 'forever' &&
      recommendedTopUpCandidate.planId === quantityKey;
    const quantity = clampForeverQuantity(foreverQuantities[quantityKey] ?? 1);
    const totalPrice = effectiveForeverPrice * quantity;
    const showQuantityControls =
      isAuth && isForeverCredits && (purchaseMode === 'direct' || enableForeverQuantitySelection);

    return (
      <div
        key={planId}
        className={[
          'pricing-plan-card',
          'pricing-plan-card--interactive',
          isCompact ? 'pricing-plan-card--compact' : '',
          isHovered ? 'pricing-plan-card--hovered' : '',
          isRecommendedForever ? 'pricing-plan-card--recommended' : '',
        ].filter(Boolean).join(' ')}
        onMouseEnter={() => setHoveredCard(planId)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <div className="pricing-plan-card-header">
          <Title level={isCompact ? 4 : 3} className="pricing-plan-card-title">
            {planDisplayName}
          </Title>
          <div className="pricing-card-badge-row">
            {isRecommendedForever ? (
              <Tag color="gold" style={{ margin: 0, fontWeight: 700 }}>
                Recommended
              </Tag>
            ) : null}
          </div>
        </div>

        <div className="pricing-plan-card-body">
          <div className="pricing-card-primary">
            {subscriberEligible && subscriberDiscountPercent > 0 && (
              <div style={{ marginTop: 4 }}>
                <Tag color="green">{subscriberDiscountPercent}% off with your subscription</Tag>
              </div>
            )}
            <div className="pricing-card-price">
              {subscriberEligible && subscriberDiscountPercent > 0 ? (
                <>
                  ${discountedPrice.toFixed(discountedPrice % 1 === 0 ? 0 : 2)}
                  <div className="pricing-card-original-price">
                    ${basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}
                  </div>
                </>
              ) : (
                <>${basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}</>
              )}
            </div>
            {!isForeverCredits && (
              <Link
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  openSavingsCalculator({
                    planName: planDisplayName,
                    planPrice: effectiveForeverPrice,
                    planCredits: foreverWordCount,
                    intervalLabel: 'Forever credits pack',
                  });
                }}
                className="pricing-card-savings-link"
              >
                Save over 90% vs typical human translation &rarr;
              </Link>
            )}
          </div>

          <div className="pricing-feature-list">
            <Space direction="vertical" size={isCompact ? 2 : 4} style={{ width: '100%' }}>
              <div className="pricing-feature-row">
                <CheckCircleOutlined style={{ color: accentSuccess, marginRight: '6px' }} />
                <Text>{formatNumber(credits)} forever credits</Text>
              </div>
              {isCompact && (
                <>
                  <div className="pricing-feature-row">
                    <CheckCircleOutlined style={{ color: accentSuccess, marginRight: '6px' }} />
                    <Text style={{ fontSize: 12 }}>Never expires</Text>
                  </div>
                  <div className="pricing-feature-row">
                    <CheckCircleOutlined style={{ color: accentSuccess, marginRight: '6px' }} />
                    <Text style={{ fontSize: 12 }}>Use any time</Text>
                  </div>
                  <div className="pricing-feature-row">
                    <CheckCircleOutlined style={{ color: accentSuccess, marginRight: '6px' }} />
                    <Text style={{ fontSize: 12 }}>Stacks with your plan</Text>
                  </div>
                </>
              )}
            </Space>
          </div>

          <div className="pricing-card-actions">
            <Space direction="vertical" size={isCompact ? 8 : 12} style={{ width: '100%' }}>
              {showQuantityControls && (
                <div className="pricing-quantity-row">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text type="secondary" className="pricing-quantity-label">
                      Bundles
                    </Text>
                    <InputNumber
                      min={1}
                      max={10}
                      value={quantity}
                      onChange={(value) => handleForeverQuantityChange(quantityKey, planForActions, planId, value)}
                      size={isCompact ? 'small' : 'middle'}
                      style={{ width: isCompact ? 80 : 96 }}
                    />
                  </div>
                  <div className="pricing-quantity-copy">
                    <Text strong>{formatCurrency(totalPrice)}</Text>
                    <div className="pricing-quantity-total-label">Total</div>
                  </div>
                </div>
              )}
              {!isAuth ? (
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() => handleCheckout(planForActions)}
                  onMouseEnter={() => setHoveredButton(`${planId}-login-purchase`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  className={`cta-button ${hoveredButton === `${planId}-login-purchase` ? 'cta-hover' : ''}`}
                  icon={<RocketOutlined className="rocket-icon" />}
                  style={{
                    height: btnHeight,
                    backgroundColor: accentPrimary,
                    ...(hoveredButton === `${planId}-login-purchase`
                      ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                      : {}),
                  }}
                >
                  Login to Purchase
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() =>
                    purchaseMode === 'direct'
                      ? handleDirectForeverPurchase(planForActions, quantityKey, quantity)
                      : handleCheckout(planForActions)
                  }
                  loading={isProcessing}
                  onMouseEnter={() => setHoveredButton(`${planId}-purchase`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  className={`cta-button ${hoveredButton === `${planId}-purchase` ? 'cta-hover' : ''}`}
                  icon={<RocketOutlined className="rocket-icon" />}
                  style={{
                    height: btnHeight,
                    backgroundColor: accentPrimary,
                    ...(hoveredButton === `${planId}-purchase`
                      ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                      : {}),
                  }}
                >
                  Purchase
                </Button>
              )}
              {!isCompact && (
                <Text type="secondary" className="pricing-card-footnote">
                  Credits never expire and can be used any time.
                </Text>
              )}
            </Space>
          </div>
        </div>
      </div>
    );
  }

  const displayPrice = subscriptionMeta
    ? interval === 'yearly'
      ? subscriptionMeta.yearlyPrice
      : subscriptionMeta.monthlyPrice
    : plan.price;
  const priceSuffix = interval === 'yearly' ? '/year' : '/month';
  const isYearly = interval === 'yearly';
  const totalAnnualCredits = subscriptionMeta?.totalAnnualCredits;
  const foreverDiscountPercent = subscriptionMeta?.foreverCreditDiscountPercent ?? 0;

  const planCreditsForCalculator = isYearly
    ? (totalAnnualCredits ?? upfrontCredits ?? credits * 12)
    : credits;
  const normalizedPlanCredits = Math.max(planCreditsForCalculator || 0, 0);
  const subscriptionIntervalLabel = isYearly ? 'Annual subscription' : 'Monthly subscription';

  return (
    <div
      key={planId}
      className={[
        'pricing-plan-card',
        'pricing-plan-card--interactive',
        isCompact ? 'pricing-plan-card--compact' : '',
        isHovered ? 'pricing-plan-card--hovered' : '',
        isPopular ? 'pricing-plan-card--featured' : '',
      ].filter(Boolean).join(' ')}
      onMouseEnter={() => setHoveredCard(planId)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      {isPopular && (
        <div className="pricing-plan-card-ribbon">
          MOST POPULAR
        </div>
      )}

      <div className="pricing-plan-card-header">
        <Title
          level={isCompact ? 4 : 3}
          className="pricing-plan-card-title"
        >
          {planDisplayName}
        </Title>
        {isPopular && (
          <CrownOutlined className="pricing-card-crown" />
        )}
        {isLegacyCurrentPlan && !planIsLegacy && (
          <div className="pricing-card-badge-row">
            <Tag color="blue">Upgrade option</Tag>
          </div>
        )}
      </div>

      <div className="pricing-plan-card-body">
        <div className="pricing-card-primary">
          <div className="pricing-card-price">
            {formatCurrency(displayPrice)}
            <span className="pricing-card-price-suffix">{priceSuffix}</span>
          </div>
          {interval === 'yearly' ? (
            <Text type='secondary'>
              Billed annually. Receive {formatNumber(upfrontCredits || totalAnnualCredits || credits * 12)} credits upfront.
            </Text>
          ) : (
            <Text type='secondary'>
              {formatNumber(credits)} credits each month.
            </Text>
          )}
          <Link
            href="#"
            onClick={(event) => {
              event.preventDefault();
              openSavingsCalculator({
                planName: planDisplayName,
                planPrice: displayPrice,
                planCredits: normalizedPlanCredits,
                intervalLabel: subscriptionIntervalLabel,
              });
            }}
            className="pricing-card-savings-link"
          >
            Save over 90% vs typical human translation &rarr;
          </Link>
        </div>

        <div className="pricing-feature-list">
          <Space direction="vertical" size={isCompact ? 2 : 6} style={{ width: '100%' }}>
            {!isYearly && (
              <div className="pricing-feature-row">
                <CalendarOutlined style={{ color: accentPrimary, marginRight: '6px' }} />
                <Text>Credits do not expire while subscribed</Text>
              </div>
            )}
            <div className="pricing-feature-row">
              {renderBooleanIcon(foreverDiscountPercent > 0)}
              <Text>
                {foreverDiscountPercent > 0
                  ? `${foreverDiscountPercent}% forever credit discount`
                : 'No forever credit discount'}
              </Text>
            </div>
            <div className="pricing-feature-row">
              {renderBooleanIcon(true)}
              <Text>Access all creative tools</Text>
            </div>
            <div className="pricing-feature-row">
              {renderBooleanIcon(true)}
              <Text>60-day subscription credit grace after cancellation</Text>
            </div>
          </Space>
        </div>

        <Paragraph className="pricing-card-ideal">
          <Text type='secondary'>Ideal for: {subscriptionMeta?.idealFor || 'Professional authors'}</Text>
        </Paragraph>

        {!isForeverCredits && isAuth && pendingChange && (isCurrent || isPendingTarget) && (
          <div className="pricing-card-state">
            <Tag color={isPendingTarget ? 'blue' : 'gold'}>
              {isPendingTarget
                ? `Scheduled ${pendingChange?.action || 'change'} on ${scheduledDate || ''}`
                : `Scheduled change on ${scheduledDate || ''}`}
            </Tag>
            {isCurrent && (
              <div style={{ marginTop: 6 }}>
                <Button
                  size="small"
                  danger
                  loading={canceling}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Cancel scheduled change?',
                      content: 'This will keep your current plan for the next billing period.',
                      okText: 'Yes, cancel',
                      okButtonProps: { danger: true },
                      onOk: cancelScheduledChange,
                    });
                  }}
                >
                  Cancel scheduled change
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="pricing-card-actions">
          {!isAuth ? (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => planForActions && handleCheckout(planForActions)}
              onMouseEnter={() => setHoveredButton(`${planId}-login-subscribe`)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                height: btnHeight,
                borderRadius: '8px',
                fontWeight: 'bold',
                backgroundColor: accentPrimary,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                ...(hoveredButton === `${planId}-login-subscribe`
                  ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                  : {}),
              }}
            >
              {renderLabel('Login to Subscribe', `${planId}-login-subscribe`)}
            </Button>
          ) : !planAvailable ? (
            <Tooltip title={unavailableTooltip}>
              <Button
                size="large"
                block
                disabled
                style={{ height: btnHeight, borderRadius: '8px', fontWeight: 'bold' }}
              >
                {unavailableLabel}
              </Button>
            </Tooltip>
          ) : !eligibilityChecked ? (
            (!isLegacyCurrentPlan && !normalizedCurrentPlanPriceId) ? (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => planForActions && handleCheckout(planForActions)}
                loading={isProcessing}
                onMouseEnter={() => setHoveredButton(`${planId}-subscribe`)}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  height: btnHeight,
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  backgroundColor: accentPrimary,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  ...(hoveredButton === `${planId}-subscribe`
                    ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                    : {}),
                }}
              >
                {renderLabel(interval === 'yearly' ? 'Subscribe (Annual)' : 'Subscribe', `${planId}-subscribe`)}
              </Button>
            ) : isCurrent ? (
              <Button
                size="large"
                block
                disabled
                style={{ height: btnHeight, borderRadius: '8px', fontWeight: 'bold' }}
              >
                Current Plan
              </Button>
            ) : isUpgradeOption ? (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => planForActions && openChangePlanModal(planForActions, 'upgrade')}
                onMouseEnter={() => setHoveredButton(`${planId}-upgrade`)}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  height: btnHeight,
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  background: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentPrimaryHover} 50%, ${accentPrimary} 100%)`,
                  backgroundSize: '200% 200%',
                  backgroundPosition: hoveredButton === `${planId}-upgrade` ? 'right center' : 'left center',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease, background-position 0.35s ease',
                  ...(hoveredButton === `${planId}-upgrade`
                    ? {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 18px rgba(24, 144, 255, 0.35), 0 0 0 3px rgba(24, 144, 255, 0.20)',
                      }
                    : {}),
                }}
              >
                {renderLabel(interval === 'yearly' ? 'Upgrade (Annual)' : 'Upgrade', `${planId}-upgrade`, <ArrowUpOutlined style={{ marginLeft: 8 }} />)}
              </Button>
            ) : isDowngradeOption ? (
              <Button
                size="large"
                block
                onClick={() => planForActions && openChangePlanModal(planForActions, 'downgrade')}
                onMouseEnter={() => setHoveredButton(`${planId}-downgrade`)}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  height: btnHeight,
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  ...(hoveredButton === `${planId}-downgrade`
                    ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                    : {}),
                }}
              >
                {renderLabel(interval === 'yearly' ? 'Downgrade (Annual)' : 'Downgrade', `${planId}-downgrade`, <ArrowDownOutlined style={{ marginLeft: 8 }} />)}
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => planForActions && handleCheckout(planForActions)}
                loading={isProcessing}
                onMouseEnter={() => setHoveredButton(`${planId}-subscribe`)}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  height: btnHeight,
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  backgroundColor: accentPrimary,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  ...(hoveredButton === `${planId}-subscribe`
                    ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                    : {}),
                }}
              >
                {renderLabel(interval === 'yearly' ? 'Subscribe (Annual)' : 'Subscribe', `${planId}-subscribe`)}
              </Button>
            )
          ) : !effectiveActive ? (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => planForActions && handleCheckout(planForActions)}
              loading={isProcessing}
              onMouseEnter={() => setHoveredButton(`${planId}-subscribe`)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                height: btnHeight,
                borderRadius: '8px',
                fontWeight: 'bold',
                backgroundColor: accentPrimary,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                ...(hoveredButton === `${planId}-subscribe`
                  ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                  : {}),
              }}
            >
              {renderLabel(interval === 'yearly' ? 'Subscribe (Annual)' : 'Subscribe', `${planId}-subscribe`)}
            </Button>
          ) : isCurrent ? (
            <Button
              size="large"
              block
              disabled
              style={{ height: btnHeight, borderRadius: '8px', fontWeight: 'bold' }}
            >
              Current Plan
            </Button>
          ) : isPendingTarget ? (
            <Tooltip
              title={`A ${pendingChange?.action || 'plan change'} is scheduled on ${scheduledDate || ''}. Click another plan to reschedule.`}
            >
              <Button
                size="large"
                block
                disabled
                style={{ height: btnHeight, borderRadius: '8px', fontWeight: 'bold' }}
              >
                Change Scheduled
              </Button>
            </Tooltip>
          ) : blocked ? (
            <Tooltip title={eligibility.reason || 'Subscription changes are currently unavailable.'}>
              <Button
                size="large"
                block
                disabled
                style={{ height: btnHeight, borderRadius: '8px', fontWeight: 'bold' }}
              >
                {isUpgradeOption
                  ? interval === 'yearly'
                    ? 'Upgrade (Annual)'
                    : 'Upgrade'
                  : isDowngradeOption
                  ? interval === 'yearly'
                    ? 'Downgrade (Annual)'
                    : 'Downgrade'
                  : 'Change Unavailable'}
              </Button>
            </Tooltip>
          ) : isUpgradeOption ? (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => planForActions && openChangePlanModal(planForActions, 'upgrade')}
              onMouseEnter={() => setHoveredButton(`${planId}-upgrade`)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                height: btnHeight,
                borderRadius: '8px',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentPrimaryHover} 50%, ${accentPrimary} 100%)`,
                backgroundSize: '200% 200%',
                backgroundPosition: hoveredButton === `${planId}-upgrade` ? 'right center' : 'left center',
                transition: 'box-shadow 0.2s ease, transform 0.2s ease, background-position 0.35s ease',
                ...(hoveredButton === `${planId}-upgrade`
                  ? {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 18px rgba(24, 144, 255, 0.35), 0 0 0 3px rgba(24, 144, 255, 0.20)',
                    }
                  : {}),
              }}
              >
                {renderLabel(interval === 'yearly' ? 'Upgrade (Annual)' : 'Upgrade', `${planId}-upgrade`, <ArrowUpOutlined style={{ marginLeft: 8 }} />)}
              </Button>
          ) : isDowngradeOption ? (
            <Button
              size="large"
              block
              onClick={() => planForActions && openChangePlanModal(planForActions, 'downgrade')}
              onMouseEnter={() => setHoveredButton(`${planId}-downgrade`)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                height: btnHeight,
                borderRadius: '8px',
                fontWeight: 'bold',
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                ...(hoveredButton === `${planId}-downgrade`
                  ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                  : {}),
              }}
            >
                {renderLabel(interval === 'yearly' ? 'Downgrade (Annual)' : 'Downgrade', `${planId}-downgrade`, <ArrowDownOutlined style={{ marginLeft: 8 }} />)}
              </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => planForActions && handleCheckout(planForActions)}
              loading={isProcessing}
              onMouseEnter={() => setHoveredButton(`${planId}-subscribe`)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                height: btnHeight,
                borderRadius: '8px',
                fontWeight: 'bold',
                backgroundColor: accentPrimary,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                ...(hoveredButton === `${planId}-subscribe`
                  ? { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(24, 144, 255, 0.25)' }
                  : {}),
              }}
            >
              {renderLabel(interval === 'yearly' ? 'Subscribe (Annual)' : 'Subscribe', `${planId}-subscribe`)}
            </Button>
          )}
          {!isCompact && (
            <Text
              type='secondary'
              className="pricing-card-footnote"
            >
              Each translated word costs one credit
            </Text>
          )}
        </div>
      </div>
    </div>
  );
});
  const renderPricingComparison = () => {
    if (isForeverCredits) {
      return null;
    }

    const featureColumnShare = 2;
    const planColumnShare = 1;
    const planCount = orderedSubscriptionMetadata.length;
    const totalShares = featureColumnShare + planColumnShare * Math.max(planCount, 1);
    const featureColumnPercent = (featureColumnShare / totalShares) * 100;
    const planColumnPercent = planCount > 0 ? (planColumnShare / totalShares) * 100 : 0;

    return (
      <details className="pricing-comparison">
        <summary>
          <span>Compare plan details</span>
          <span className="pricing-comparison-helper">Prices, credits, discounts, and included options</span>
        </summary>
        <div className="pricing-comparison-scroll">
          <table className="pricing-comparison-table">
            <colgroup>
              <col style={{ width: `${featureColumnPercent}%` }} />
              {orderedSubscriptionMetadata.map((meta) => (
                <col key={`${meta.key}-col`} style={{ width: `${planColumnPercent}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>Feature / Perk</th>
                {orderedSubscriptionMetadata.map((meta) => (
                  <th key={`${meta.key}-head`}>{meta.displayName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Monthly Price</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-monthly-price`}>{formatCurrency(meta.monthlyPrice)}</td>
                ))}
              </tr>
              <tr>
                <td>Annual Price (2 months free)</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-annual-price`}>{formatCurrency(meta.yearlyPrice)}</td>
                ))}
              </tr>
              <tr>
                <td>Monthly Credits</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-monthly-credits`}>{formatNumber(meta.monthlyCredits)}</td>
                ))}
              </tr>
              <tr>
                <td>Subscription Credit Expiration</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-expiration`}>
                    Protected while subscribed; 60-day grace after cancellation
                  </td>
                ))}
              </tr>
              <tr>
                <td>Discount on Forever Credits</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-discount`}>
                    <div className="pricing-feature-row">
                      {renderBooleanIcon(meta.foreverCreditDiscountPercent > 0)}
                      <span>{meta.foreverCreditDiscountPercent > 0 ? `${meta.foreverCreditDiscountPercent}%` : 'None'}</span>
                    </div>
                  </td>
                ))}
              </tr>
              {['Access All Creative Tools', 'Early Renewal Option', 'Annual Plan Available (All Credits Upfront)'].map((label) => (
                <tr key={label}>
                  <td>{label}</td>
                  {orderedSubscriptionMetadata.map((meta) => (
                    <td key={`${meta.key}-${label}`}>{renderBooleanIcon(true)}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td>Ideal For</td>
                {orderedSubscriptionMetadata.map((meta) => (
                  <td key={`${meta.key}-ideal`}>{meta.idealFor || '-'}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    );
  };

  const renewalRecommendationSelected =
    shouldShowTopUpRecommendation && recommendedTopUpCandidate?.kind === 'renewal';
  if (renderMode === 'foreverWithSide') {
    return (
      <div
        className={[
          'custom-pricing',
          'custom-pricing--forever',
          isCompact ? 'custom-pricing--compact' : '',
          'custom-pricing-shell',
        ].filter(Boolean).join(' ')}
      >
        {shouldShowTopUpRecommendation && (
          <Alert
            type="info"
            showIcon
            message="Recommended Option"
            description={`You need ${formatNumber(normalizedShortfallCredits)} more credits to continue. ${topUpRecommendationMessage}`}
            style={{ marginBottom: 4 }}
          />
        )}
        {renderPricingIntervalToggle()}
        {!isForeverCredits && isLegacyCurrentPlan && (
          <Alert
            type="warning"
            showIcon
            message="You're on a legacy plan"
            description="Legacy plans are no longer available. You can keep yours as long as you stay subscribed, or choose any plan below to upgrade to the latest offering."
            style={{ marginBottom: 8 }}
          />
        )}
        <div className="pricing-side-layout">
          <div className="pricing-cards-grid">
            {cards}
          </div>

          {/* Side panel */}
          <div
            onMouseEnter={() => setSidePanelHovered(true)}
            onMouseLeave={() => setSidePanelHovered(false)}
            className={[
              'pricing-side-panel',
              sidePanelHovered ? 'pricing-side-panel--hovered' : '',
              renewalRecommendationSelected ? 'pricing-side-panel--recommended' : '',
            ].filter(Boolean).join(' ')}
          >
            {/* Header */}
            <div className="pricing-side-panel-header">
              <Title level={4}>
                {sidePanel?.type === 'currentPlan' ? 'Early Renewal' : 'Subscribe'}
              </Title>
              <div className="pricing-card-badge-row">
                {renewalRecommendationSelected ? (
                  <Tag color="gold" style={{ margin: 0, fontWeight: 700 }}>
                    Recommended
                  </Tag>
                ) : null}
              </div>
            </div>

            {/* Body */}
            <div className="pricing-side-panel-body">
              {sidePanel?.type === 'currentPlan'
                ? (() => {
                    const pc = sidePanel?.pendingChange;
                    const tp = pc
                      ? subscriptionPlansLookup.find(
                          (p) =>
                            (pc.targetPriceId && p.stripePriceID === pc.targetPriceId) ||
                            (pc.targetProductId && p.stripeProductID === pc.targetProductId)
                        )
                      : undefined;
                    const effectivePlanName = tp?.planName || sidePanel?.planName || 'Plan';
                    const intervalRaw = (
                      tp?.billingInterval ||
                      sidePanel?.billingInterval ||
                      currentPlanMetadata?.billingInterval ||
                      currentPlanLookup?.billingInterval ||
                      ''
                    )
                      .toString()
                      .toLowerCase();
                    const intervalMonthsCandidate = pickDefined(
                      tp?.intervalMonths,
                      sidePanel?.intervalMonths,
                      currentPlanMetadata?.intervalMonths,
                      currentPlanLookup?.intervalMonths
                    );
                    const intervalMonthsFallback = intervalRaw.startsWith('year') ? 12 : 1;
                    const resolvedIntervalMonths = (() => {
                      const numeric = ensureNumber(intervalMonthsCandidate, intervalMonthsFallback);
                      return numeric > 0 ? numeric : intervalMonthsFallback;
                    })();
                    const isAnnualPlan =
                      resolvedIntervalMonths >= 12 ||
                      ['year', 'yearly', 'annual'].includes(intervalRaw);
                    const resolvedMonthlyCredits = (() => {
                      if (tp) {
                        return ensureNumber(
                          pickDefined(
                            tp.monthlyCredits,
                            tp.monthlyEquivalentCredits,
                            tp.creditsPerInterval && resolvedIntervalMonths > 0
                              ? tp.creditsPerInterval / resolvedIntervalMonths
                              : undefined
                          ),
                          0
                        );
                      }
                      if (sidePanel?.monthlyCreditsAllocated && sidePanel.monthlyCreditsAllocated > 0) {
                        return sidePanel.monthlyCreditsAllocated;
                      }
                      const metadataMonthly = pickDefined(
                        currentPlanMetadata?.monthlyCredits,
                        currentPlanMetadata?.totalAnnualCredits
                          ? Math.floor(currentPlanMetadata.totalAnnualCredits / 12)
                          : undefined,
                        currentPlanLookup?.monthlyCredits,
                        currentPlanLookup?.monthlyEquivalentCredits,
                        currentPlanLookup?.creditsPerInterval &&
                          resolvedIntervalMonths > 0
                          ? currentPlanLookup.creditsPerInterval / resolvedIntervalMonths
                          : undefined
                      );
                      return ensureNumber(metadataMonthly, 0);
                    })();
                    const totalIntervalCredits = (() => {
                      const candidate = pickDefined(
                        sidePanel?.upfrontCredits,
                        tp?.creditsPerInterval,
                        tp?.monthlyEquivalentCredits && resolvedIntervalMonths > 0
                          ? tp.monthlyEquivalentCredits * resolvedIntervalMonths
                          : undefined,
                        currentPlanMetadata?.totalAnnualCredits,
                        currentPlanLookup?.creditsPerInterval
                      );
                      const fallbackTotal =
                        resolvedMonthlyCredits *
                        Math.max(resolvedIntervalMonths || (isAnnualPlan ? 12 : 1), 1);
                      return Math.max(ensureNumber(candidate, fallbackTotal), 0);
                    })();
                    const creditsLabel = isAnnualPlan
                      ? `Total credits delivered now: ${totalIntervalCredits.toLocaleString()}`
                      : `Monthly credits: ${Math.max(resolvedMonthlyCredits, 0).toLocaleString()}`;
                    const resolvedPrice = (() => {
                      if (tp && typeof tp.price === 'number') {
                        return tp.price;
                      }
                      if (sidePanel?.monthlyPrice && sidePanel.monthlyPrice > 0) {
                        return sidePanel.monthlyPrice;
                      }
                      if (
                        typeof currentPlanMetadata?.monthlyPrice === 'number' &&
                        currentPlanMetadata.monthlyPrice > 0
                      ) {
                        return currentPlanMetadata.monthlyPrice;
                      }
                      if (
                        currentPlanLookup &&
                        typeof currentPlanLookup.price === 'number' &&
                        currentPlanLookup.price > 0
                      ) {
                        return currentPlanLookup.price;
                      }
                      return null;
                    })();
                    const priceSuffix = isAnnualPlan ? 'year' : 'month';
                    const renewalMessage = pc
                      ? `Renewing will apply your scheduled change${
                          tp ? ` to ${tp.planName}` : ''
                        }, move your billing date to today, and ${
                          isAnnualPlan
                            ? 'deliver the full annual credits immediately.'
                            : 'issue credits immediately.'
                        }`
                      : isAnnualPlan
                      ? 'Renewing will move your billing date to today, charge you immediately, and deliver your full annual credits right away.'
                      : 'Renewing will move your billing date to today, charge you immediately, and issue credits as indicated above.';
                    return (
                      <>
                        <div style={{ textAlign: 'center', margin: '5px 0 8px' }}>
                          <Tag color="blue">{effectivePlanName}</Tag>
                        </div>

                        <div style={{ padding: '0 6px', margin: '10px 0 12px' }}>
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleOutlined style={{ color: accentSuccess, marginRight: 6 }} />
                              <Text>{creditsLabel}</Text>
                            </div>

                            {resolvedPrice && resolvedPrice > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <DollarOutlined style={{ color: accentPrimary, marginRight: 6 }} />
                                <Text>
                                  Price: {formatCurrency(resolvedPrice)} / {priceSuffix}
                                </Text>
                              </div>
                            ) : null}
                          </Space>
                        </div>

                        <div style={{ padding: '0 6px', marginTop: 6, textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {renewalMessage}
                          </Text>
                        </div>

                        <div style={{ textAlign: 'center', padding: '0 6px', marginTop: 12 }}>
                          <Tooltip
                            title={
                              sidePanel?.renewDisabled
                                ? sidePanel?.renewDisabledTooltip || 'Early renewal is not available yet.'
                                : 'This will charge your plan immediately and set your billing date to today.'
                            }
                          >
                            <Button
                              type="primary"
                              size="large"
                              className="renew-now-btn"
                              icon={<RocketOutlined className="rocket-icon" />}
                              onClick={() => sidePanel?.onRenewNow && sidePanel.onRenewNow()}
                              block
                              disabled={!!sidePanel?.renewDisabled}
                              style={{
                                height: 36,
                                borderRadius: 8,
                                fontWeight: 'bold',
                                backgroundColor: sidePanel?.renewDisabled ? undefined : accentPrimary,
                                borderColor: sidePanel?.renewDisabled ? undefined : accentPrimary,
                              }}
                            >
                              Renew Plan Now
                            </Button>
                          </Tooltip>
                        </div>
                      </>
                    );
                  })()
                : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Tag color="blue">No Active Plan</Tag>
                  </div>

                  <div style={{ textAlign: 'center', margin: '8px 0' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Choose a plan to receive monthly credits.
                    </Text>
                  </div>

                  <div style={{ textAlign: 'center', padding: '0 6px' }}>
                    <Button
                      type="primary"
                      icon={<CreditCardOutlined />}
                      onClick={() => sidePanel?.onSubscribe && sidePanel.onSubscribe()}
                      block
                      style={{
                        height: 36,
                        borderRadius: 8,
                        fontWeight: 'bold',
                        backgroundColor: accentPrimary,
                      }}
                    >
                      Subscribe
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }



  const affiliateCodeModal = (
    <Modal
      title="Have an affiliate code?"
      open={referralModalVisible}
      onCancel={handleReferralSkip}
      footer={[
        <Button key="skip" onClick={handleReferralSkip} disabled={referralApplyLoading}>
          Skip & Continue
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleReferralApply}
          loading={referralApplyLoading}
        >
          Apply & Continue
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text>
          Enter your friend’s affiliate code to unlock bonus credits for both of you after your first successful subscription payment.
        </Text>
        <Input
          autoFocus
          placeholder="Affiliate code"
          value={referralCodeInput}
          onChange={handleReferralCodeChange}
          maxLength={15}
          disabled={referralApplyLoading}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          You can skip this step if you don’t have a code yet.
        </Text>
      </Space>
    </Modal>
  );

  const savingsCalculatorModal = (
    <Modal
      title="Compare to Human Translation"
      open={calculatorVisible}
      onCancel={closeSavingsCalculator}
      footer={null}
      destroyOnClose
    >
      {calculatorContext ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{calculatorContext.planName}</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {calculatorContext.intervalLabel}
            </Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              Includes {formatNumber(calculatorContext.planCredits)} credits.
            </Text>
          </div>
          <div>
            <Text strong>Language</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={calculatorLanguage || HUMAN_TRANSLATION_RATE_BANDS[0]?.key}
              onChange={(value) => setCalculatorLanguage(value)}
              options={HUMAN_TRANSLATION_RATE_BANDS.map((band) => ({ value: band.key, label: band.label }))}
            />
          </div>
          <div>
            <Text strong>Word count</Text>
            <InputNumber
              style={{ width: '100%', marginTop: 6 }}
              min={0}
              step={500}
              value={calculatorWordCount}
              onChange={(value) => setCalculatorWordCount(Math.max(0, Number(value) || 0))}
            />
          </div>
          <div>
            <Text>Typical human translator cost:</Text>
            <Text strong style={{ display: 'block' }}>
              {formatCurrency(calculatorHumanLow)} - {formatCurrency(calculatorHumanHigh)}
            </Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              Typical rates used: {formatCurrency(calculatorHumanRateLow)} - {formatCurrency(calculatorHumanRateHigh)} per word.
            </Text>
            <Text style={{ display: 'block', marginTop: 8 }}>
              Cost with {calculatorContext.planName} {calculatorContext.intervalLabel.toLowerCase()}:
            </Text>
            <Text strong style={{ display: 'block' }}>
              {formatCurrency(calculatorPlanCostEstimate)}
            </Text>
            <Text style={{ display: 'block', marginTop: 8 }}>Estimated savings:</Text>
            <Text strong style={{ display: 'block' }}>
              {formatCurrency(calculatorSavingsLow)} - {formatCurrency(calculatorSavingsHigh)}
            </Text>
            {!calculatorHasPlanData && (
              <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                Plan cost comparison assumes one credit per word using the displayed plan price.
              </Text>
            )}
          </div>
          <Text type="secondary" style={{ display: 'block' }}>
            Rates reflect typical professional translator quotes for {selectedCalculatorRate?.label}.
          </Text>
          <Text type="secondary" style={{ display: 'block' }}>
            * Japanese pricing shown as a per-word equivalent.
          </Text>
        </Space>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      )}
    </Modal>
  );


  if (renderMode === 'cardsOnly') {

    // Return only the cards without any wrapping container or banners/modals

    return <>{cards}{affiliateCodeModal}{savingsCalculatorModal}</>;

  }



  return (

    <div
      className={[
        'custom-pricing',
        isForeverCredits ? 'custom-pricing--forever' : 'custom-pricing--subscription',
        pricingSurface === 'account' ? 'custom-pricing--account' : '',
        isCompact ? 'custom-pricing--compact' : '',
      ].filter(Boolean).join(' ')}
    >
      {affiliateCodeModal}
      {savingsCalculatorModal}

      {shouldShowPromoStrip && wordsTickerReady && false && (
        <div
          style={{
            textAlign: 'center',
            margin: isCompact ? '8px 0' : '12px 0',
          }}
        >
          <Text type='secondary' style={{ fontVariantNumeric: 'tabular-nums', color: accentPrimary, fontSize: isCompact ? 12 : 13 }}>
            Words translated with ScribeShadow: {formattedWordsTranslated}
          </Text>
        </div>
      )}

      {shouldShowPromoStrip && showTestimonialSnippet && testimonialSnippet && (
        <div
          style={{
            margin: isCompact ? '0 0 12px' : '0 auto 16px',
            maxWidth: isCompact ? '100%' : '960px',
            padding: isCompact ? '0 8px' : '0 16px',
          }}
        >
        <div
          style={{
            textAlign: 'center',
            margin: isCompact ? '8px 0' : '12px 0',
          }}
        >
        <strong>What authors are saying</strong>
        </div>          
          <div
            style={{
              background: surfaceInfo,
              border: `1px solid ${borderInfo}`,
              borderRadius: 12,
              padding: isCompact ? '8px 12px' : '12px 20px',
              maxWidth: isCompact ? '100%' : 1080,
              margin: '0 auto',
              textAlign: 'center',
              minHeight: isCompact ? 0 : 88,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Text type='secondary' style={{ fontStyle: 'italic' }}>
              "{testimonialSnippet}"
            </Text>
            {testimonialAuthor && (
              <Text strong style={{ marginTop: 10 }}>- {testimonialAuthor}</Text>
            )}
          </div>
        </div>
      )}

      {renderPricingIntervalToggle()}

      {!isForeverCredits && isLegacyCurrentPlan && (
        <Alert
          type="warning"
          showIcon
          message="You're on a legacy plan"
          description="Legacy plans are no longer available. You can keep yours as long as you stay subscribed, or choose any plan below to upgrade to the latest offering."
          style={{ marginBottom: 16 }}
        />
      )}


      {renderPricingActionBanner()}



<div className="pricing-cards-grid">

  {cards}

</div>

      {renderPricingComparison()}

      <Modal

        centered

        width={620}

        open={changeModalVisible}

        title={

          <div className="pricing-modal-title">

            {changeAction === 'upgrade' ? (

              <ArrowUpOutlined style={{ color: accentSuccess }} />

            ) : (

              <ArrowDownOutlined style={{ color: accentWarning }} />

            )}

            <span>{changeAction === 'upgrade' ? `Upgrade to ${changeModalPlan?.planName}` : `Downgrade to ${changeModalPlan?.planName}`}</span>

          </div>

        }

        onCancel={() => setChangeModalVisible(false)}

        onOk={submitChangePlan}

        okButtonProps={{ disabled: submittingChange || modalBlocked || (changeAction === 'downgrade' && selectedTiming === 'now'), loading: submittingChange }}

        okText={

          selectedTiming === 'now'

            ? (changeAction === 'upgrade' ? 'Upgrade now' : 'Downgrade now')

            : (changeAction === 'upgrade' ? 'Upgrade at the end of the billing period' : 'Downgrade at the end of the billing period')

        }

      >

        <div className="pricing-modal-body">

          {loadingDetails ? (

            <div style={{ textAlign: 'center', padding: '12px' }}>

              <Spin />

            </div>

          ) : (

            <>

              {/* Plan summary */}
              <div className="pricing-modal-summary">
                <div className="pricing-modal-summary-row">

                  <Text strong>Plan change summary</Text>

                  <Tag color={changeAction === 'upgrade' ? 'green' : 'orange'} style={{ marginLeft: 8 }}>

                    {changeAction === 'upgrade' ? 'Upgrade' : 'Downgrade'}

                  </Tag>

                </div>

                <div style={{ marginTop: 6 }}>

                  <Text type='secondary'>From </Text>

                  <Text>{subscriptionDetails?.plan_name || 'Current plan'}</Text>

                  <Text type='secondary'> to </Text>

                  <Text>{changeModalPlan?.planName}</Text>

                </div>

                <div style={{ marginTop: 4 }}>

                  <Text type='secondary'>

                    {selectedTiming === 'now'

                      ? 'Charge now; billing cycle resets today; credits refresh immediately.'

                      : `Takes effect on ${formatDate(subscriptionDetails?.next_payment_date || subscriptionDetails?.current_period_end)}. No charge now.`}

                  </Text>

                </div>

              </div>



              <Radio.Group onChange={(e) => setSelectedTiming(e.target.value)} value={selectedTiming}>

                <Space direction="vertical">

                  <Radio value="now" disabled={changeAction === 'downgrade'}>

                    {changeAction === 'upgrade' ? 'Upgrade now' : 'Downgrade now'}{' '}

                    {changeAction === 'downgrade' && (

                      <Text type='secondary' style={{ marginLeft: 8 }}>

                        (Downgrade now is not allowed)

                      </Text>

                    )}

                  </Radio>

                  <Radio value="next_cycle">

                    {changeAction === 'upgrade' ? 'Upgrade at the end of the billing period' : 'Downgrade at the end of the billing period'} (

                    {formatDate(subscriptionDetails?.next_payment_date || subscriptionDetails?.current_period_end)})

                  </Radio>

                </Space>

              </Radio.Group>



              {/* Non-blocking warnings/info */}

              {!!subscriptionDetails?.renewal_proximity_warning && (

                <Alert type="warning" showIcon style={{ marginTop: 12 }} message={subscriptionDetails.renewal_proximity_warning} />

              )}

              {!!subscriptionDetails?.renewal_wait_message && (

                <Alert type="info" showIcon style={{ marginTop: 12 }} message={subscriptionDetails.renewal_wait_message} />

              )}



              <div style={{ marginTop: 12 }}>

                {selectedTiming === 'now' ? (

                  <Alert

                    type="warning"

                    showIcon

                    message="You will be charged immediately"

                    description="You'll be charged the full plan price now, your billing cycle will reset to today, and your monthly credits will refresh immediately."

                  />

                ) : (

                  <Alert

                    type="info"

                    showIcon

                    message="No charge now"

                    description={`Your plan change will take effect on ${formatDate(subscriptionDetails?.next_payment_date || subscriptionDetails?.current_period_end)}.`}

                  />

                )}

              </div>



              {modalError && (

                <div style={{ marginTop: 12 }}>

                  <Alert

                    type="error"

                    showIcon

                    message={modalError}

                    action={

                      <Button

                        type="link"

                        style={{ paddingLeft: 0 }}

                        onClick={() => {

                          try {

                            window.location.href = '/account';

                          } catch {

                            // no-op

                          }

                        }}

                      >

                        Go to Account

                      </Button>

                    }

                  />

                </div>

              )}

            </>

          )}

        </div>

      </Modal>

    </div>

  );

};
